<template>
  <div class="cursos-section-container">
    <div class="section-content">
      <!-- Title -->
      <h2 class="section-title mb-2">{{ config.title }}</h2>
      
      <!-- Subtitle -->
      <p class="section-subtitle mb-4">
        {{ config.subtitle }}
      </p>
      
      <!-- Loading State for Courses -->
      <div v-if="loading" class="loading-courses">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Cargando cursos...</span>
        </div>
        <p class="mt-2">Cargando cursos disponibles para este mes...</p>
      </div>
      
      <!-- No Available Courses Warning -->
      <div v-else-if="!hasMaterias || !hasAvailableSchedules" class="no-courses-warning">
        <div class="warning-card">
          <div class="warning-header">
            <i class="warning-icon">⚠️</i>
            <span class="warning-title">Cupos agotados en esta sede para estos cursos:</span>
          </div>
          
          <div class="unavailable-courses-list">
            <ul>
              <li>Ciencias Sociales</li>
              <li>Física</li>
              <li>Biología</li>
              <li>Química</li>
              <li>Física Mención</li>
              <li>Biología Mención</li>
              <li>Química Mención</li>
              <li>Matemática Electivo</li>
            </ul>
          </div>
          
          <div class="streaming-option">
            <p>Puedes buscarlos en otra sede u optar por la <span class="streaming-text">modalidad streaming</span></p>
          </div>
          
          <div class="faq-option">
            <p>Si requieres más información puedes consultar en tus <a href="https://www.preupdv.cl/preguntas-frecuentes" target="_blank" class="faq-link">preguntas frecuentes</a></p>
          </div>
        </div>
      </div>
      
      <!-- Courses Grid -->
      <div v-else class="courses-grid">
        <div class="row">
          <!-- Left Column - Main Courses -->
          <div class="col-md-6">
            <div class="mb-3" v-for="materia in groupedLeftMaterias" :key="`${materia.subjectName}_${materia.courseId}`">
              <div class="course-item">
                <button 
                  class="course-button"
                  :class="isCourseExpanded(`${materia.subjectName}_${materia.courseId}`) ? 'course-button-active' : 'course-button-inactive'"
                  :style="getButtonStyle(`${materia.subjectName}_${materia.courseId}`)"
                  @click="toggleCourseExpansion(`${materia.subjectName}_${materia.courseId}`)"
                >
                  <span>{{ getFormattedCourseName(materia) }}</span>
                  
                  <!-- Badge antes del ícono -->
                  <span 
                    v-if="getSubjectTypeForMateria(materia)"
                    class="inline-badge"
                    :class="{
                      'badge-mencion': getSubjectTypeForMateria(materia) === 'MENCIÓN',
                      'badge-electivo': getSubjectTypeForMateria(materia) === 'ELECTIVO',
                      'badge-comun': getSubjectTypeForMateria(materia) === 'Común'
                    }"
                  >
                    {{ getSubjectTypeForMateria(materia) }}
                  </span>
                  
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" class="course-arrow" :class="{ 'rotated': isCourseExpanded(`${materia.subjectName}_${materia.courseId}`) }">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                
                <!-- Course Schedule Options -->
                <div class="course-schedule-options" v-if="isCourseExpanded(`${materia.subjectName}_${materia.courseId}`)">
                  <div v-if="materia.allSchedules && materia.allSchedules.length > 0" class="available-schedules">
                    <!-- Dynamic Schedule Columns -->
                    <div class="schedule-columns">
                      <div 
                        v-for="dayGroup in getDayGroups(materia.allSchedules)" 
                        :key="dayGroup.days.join('-')" 
                        class="schedule-column"
                      >
                        <div class="column-header">
                          <span class="day-header">{{ formatDayGroup(dayGroup.days) }}</span>
                        </div>
                        <div class="column-times">
                          <div 
                            v-for="(courseTimeSlot, index) in dayGroup.courseTimeSlots" 
                            :key="`${courseTimeSlot.courseId}_${index}`"
                            class="time-slot-btn"
                            :class="[
                              timeSlotClasses(courseTimeSlot),
                              { 'blocked': isMatematicaElectivoBlocked(courseTimeSlot.courseId) || isCommonCourseBlocked(courseTimeSlot.courseId) }
                            ]"
                            :style="courseTimeSlot.allSchedules.some(s => isScheduleSelected(s.courseId, s.id)) ? { backgroundColor: getRandomColor(`${courseTimeSlot.courseId}-${courseTimeSlot.subjectName}`) } : {}"
                            :title="getMatematicaElectivoTooltip(courseTimeSlot.courseId) || getCommonCourseTooltip(courseTimeSlot.courseId)"
                            @click="handleCourseTimeSlotClick(dayGroup, courseTimeSlot)"
                          >
                            <div class="time-text">
                              <div v-if="courseTimeSlot.allSchedules.some(s => hasScheduleConflict(s.courseId, s.id) && !isScheduleSelected(s.courseId, s.id))" class="conflict-header">
                                <span class="warning-icon-inline">⚠️</span>
                                <span class="conflict-label">Conflicto</span>
                              </div>
                              <div class="time-content" :class="{ 'has-conflict': courseTimeSlot.allSchedules.some(s => hasScheduleConflict(s.courseId, s.id) && !isScheduleSelected(s.courseId, s.id)) }">
                                <span v-html="courseTimeSlot.displayText"></span>
                              </div>
                            </div>
                            <div v-if="isLastSeatsForCourseTimeSlot(courseTimeSlot)" class="last-seats-warning" :class="{ 'selected': courseTimeSlot.allSchedules.some(s => isScheduleSelected(s.courseId, s.id)) }">
                              <span class="warning-text">⚠️ Últimos cupos</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Mensaje de no horarios disponibles -->
                  <div v-else class="no-schedules-message">
                    <p class="text-muted">No tiene horarios disponibles este curso.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Right Column - Additional Courses -->
          <div class="col-md-6">
            <div class="mb-3" v-for="materia in groupedRightMaterias" :key="`${materia.subjectName}_${materia.courseId}`">
              <div class="course-item">
                <button 
                  class="course-button"
                  :class="isCourseExpanded(`${materia.subjectName}_${materia.courseId}`) ? 'course-button-active' : 'course-button-inactive'"
                  :style="getButtonStyle(`${materia.subjectName}_${materia.courseId}`)"
                  @click="toggleCourseExpansion(`${materia.subjectName}_${materia.courseId}`)"
                >
                  <span>{{ getFormattedCourseName(materia) }}</span>
                  
                  <!-- Badge antes del ícono -->
                  <span 
                    v-if="getSubjectTypeForMateria(materia)"
                    class="inline-badge"
                    :class="{
                      'badge-mencion': getSubjectTypeForMateria(materia) === 'MENCIÓN',
                      'badge-electivo': getSubjectTypeForMateria(materia) === 'ELECTIVO',
                      'badge-comun': getSubjectTypeForMateria(materia) === 'Común'
                    }"
                  >
                    {{ getSubjectTypeForMateria(materia) }}
                  </span>
                  
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" class="course-arrow" :class="{ 'rotated': isCourseExpanded(`${materia.subjectName}_${materia.courseId}`) }">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                
                <!-- Course Schedule Options -->
                <div class="course-schedule-options" v-if="isCourseExpanded(`${materia.subjectName}_${materia.courseId}`)">
                  <div v-if="materia.allSchedules && materia.allSchedules.length > 0" class="available-schedules">
                    <!-- Dynamic Schedule Columns -->
                    <div class="schedule-columns">
                      <div 
                        v-for="dayGroup in getDayGroups(materia.allSchedules)" 
                        :key="dayGroup.days.join('-')" 
                        class="schedule-column"
                      >
                        <div class="column-header">
                          <span class="day-header">{{ formatDayGroup(dayGroup.days) }}</span>
                        </div>
                        <div class="column-times">
                          <div 
                            v-for="(courseTimeSlot, index) in dayGroup.courseTimeSlots" 
                            :key="`${courseTimeSlot.courseId}_${index}`"
                            class="time-slot-btn"
                            :class="[
                              timeSlotClasses(courseTimeSlot),
                              { 'blocked': isMatematicaElectivoBlocked(courseTimeSlot.courseId) || isCommonCourseBlocked(courseTimeSlot.courseId) }
                            ]"
                            :style="courseTimeSlot.allSchedules.some(s => isScheduleSelected(s.courseId, s.id)) ? { backgroundColor: getRandomColor(`${courseTimeSlot.courseId}-${courseTimeSlot.subjectName}`) } : {}"
                            :title="getMatematicaElectivoTooltip(courseTimeSlot.courseId) || getCommonCourseTooltip(courseTimeSlot.courseId)"
                            @click="handleCourseTimeSlotClick(dayGroup, courseTimeSlot)"
                          >
                            <div class="time-text">
                              <div v-if="courseTimeSlot.allSchedules.some(s => hasScheduleConflict(s.courseId, s.id) && !isScheduleSelected(s.courseId, s.id))" class="conflict-header">
                                <span class="warning-icon-inline">⚠️</span>
                                <span class="conflict-label">Conflicto</span>
                              </div>
                              <div class="time-content" :class="{ 'has-conflict': courseTimeSlot.allSchedules.some(s => hasScheduleConflict(s.courseId, s.id) && !isScheduleSelected(s.courseId, s.id)) }">
                                <span v-html="courseTimeSlot.displayText"></span>
                              </div>
                            </div>
                            <div v-if="isLastSeatsForCourseTimeSlot(courseTimeSlot)" class="last-seats-warning" :class="{ 'selected': courseTimeSlot.allSchedules.some(s => isScheduleSelected(s.courseId, s.id)) }">
                              <span class="warning-text">⚠️ Últimos cupos</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Mensaje de no horarios disponibles -->
                  <div v-else class="no-schedules-message">
                    <p class="text-muted">No tiene horarios disponibles este curso.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      
      <!-- Weekly Schedule Calendar -->
      <WeeklyCalendar
        :selected-courses="localSelectedCourses"
        :left-column-materias="groupedLeftMaterias"
        :right-column-materias="groupedRightMaterias"
        :read-only="false"
      />
      
      <!-- Modal de Confirmación de Conflicto de Horarios -->
      <div v-if="showConflictModal" class="conflict-modal-overlay" @click="closeConflictModal">
        <div class="conflict-modal" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="warning-icon">⚠️</i>
              Conflicto de Horarios
            </h5>
            <button type="button" class="close-btn" @click="closeConflictModal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p class="conflict-message">
              El horario seleccionado se cruza con otro curso ya seleccionado:
            </p>
            <div class="conflict-details">
              <div class="existing-course">
                <strong>Curso actual:</strong> {{ conflictData.existingCourse?.name }}
                <br>
                <span class="schedule-time">{{ formatTime(conflictData.existingSchedule?.startTime) }} - {{ formatTime(conflictData.existingSchedule?.endTime) }} {{ conflictData.existingSchedule?.dayOfWeek }}</span>
              </div>
              <div class="new-course">
                <strong>Nuevo curso:</strong> {{ conflictData.newCourse?.name }}
                <br>
                <span class="schedule-time">{{ formatTime(conflictData.newSchedule?.startTime) }} - {{ formatTime(conflictData.newSchedule?.endTime) }} {{ conflictData.newSchedule?.dayOfWeek }}</span>
              </div>
            </div>
            <p class="conflict-warning">
              ¿Deseas reemplazar el horario actual por el nuevo?
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeConflictModal">
              Cancelar
            </button>
            <button type="button" class="btn btn-warning" @click="confirmScheduleReplacement">
              Reemplazar Horario
            </button>
          </div>
        </div>
      </div>

      <!-- Modal de Validación de Matemática (M2) Electivo -->
      <div v-if="showMatematicaElectivoModal" class="conflict-modal-overlay" @click="closeMatematicaElectivoModal">
        <div class="conflict-modal" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="warning-icon">ℹ️</i>
              Validación Requerida
            </h5>
            <button type="button" class="close-btn" @click="closeMatematicaElectivoModal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p class="conflict-message">
              Has seleccionado Matemática (M2) Electivo. Para continuar también necesitas seleccionar un horario de Matemática (M1).
            </p>
            <div class="conflict-details">
              <div class="validation-info">
                <strong>Requisito:</strong> Matemática (M1) es obligatoria para poder continuar con Matemática (M2) Electivo.
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" @click="closeMatematicaElectivoModal">
              Entendido
            </button>
          </div>
        </div>
      </div>


    </div>
  </div>
</template>

<script>
import WeeklyCalendar from './WeeklyCalendar.vue'
import courseColorsStore from '@/store/courseColorsStore'
import { formatSubjectName, formatFullCourseName } from '@/utils/subjectNameUtils'
export default {
  name: 'CourseSelector',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    leftColumnMaterias: {
      type: Array,
      default: () => []
    },
    rightColumnMaterias: {
      type: Array,
      default: () => []
    },
    selectedCourses: {
      type: Array,
      default: () => []
    },
    showDebug: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expandedCourses: [], // Cursos expandidos para mostrar horarios
      localSelectedCourses: [], // Estado local de cursos seleccionados
      showConflictModal: false, // Control del modal de conflicto
      conflictData: {}, // Datos del conflicto actual
      pendingScheduleSelection: null, // Horario pendiente de selección
      showMatematicaElectivoModal: false, // Control del modal de Matemática Electivo
      config: {
        title: "Cursos",
        subtitle: "Selecciona los cursos que quieres agregar a tu programa"
      },
      // Constantes para nombres de cursos
      COURSE_NAMES: {
        MATEMATICA: 'Matemática (M1)',
        MATEMATICA_ELECTIVO: 'Matemática (M2) Electivo',
        LENGUAJE: 'Competencia Lectora',
        BIOLOGIA: 'Biología',
        FISICA: 'Física',
        QUIMICA: 'Química',
        CIENCIAS_SOCIALES: 'Ciencias Sociales',
        BIOLOGIA_MENCION: 'Biología Mención',
        FISICA_MENCION: 'Física Mención',
        QUIMICA_MENCION: 'Química Mención'
      },
      // Datos para el calendario semanal
      weekDays: [
        { short: 'LUN', full: 'Lunes' },
        { short: 'MAR', full: 'Martes' },
        { short: 'MIÉ', full: 'Miércoles' },
        { short: 'JUE', full: 'Jueves' },
        { short: 'VIE', full: 'Viernes' },
        { short: 'SÁB', full: 'Sábado' }
      ],
      timeSlots: ['05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00', '23:00']
    }
  },
  computed: {
    // Combinar todas las materias de ambas columnas
    allMaterias() {
      return [...(this.leftColumnMaterias || []), ...(this.rightColumnMaterias || [])];
    },
    
    totalMaterias() {
      return this.allMaterias.length;
    },
    
    hasMaterias() {
      return this.totalMaterias > 0;
    },
    
    hasAvailableSchedules() {
      return this.groupedLeftMaterias.length > 0 || this.groupedRightMaterias.length > 0;
    },
    
    // Separar cursos comunes (hasMention = false) para la columna izquierda
    groupedLeftMaterias() {
      const materiasProcessed = this.separateCoursesByMention();
      const filtered = materiasProcessed.common.filter(materia => 
        materia.allSchedules && materia.allSchedules.length > 0
      );
      // Remover Ciencias Sociales de la columna izquierda
      const withoutCienciasSociales = filtered.filter(materia => 
        materia.subjectName !== 'Ciencias Sociales' && materia.displayName !== 'Ciencias Sociales'
      );
      const sorted = this.sortCoursesByCustomOrder(withoutCienciasSociales, 'common');
      // Limitar a máximo 5 cursos por columna
      return sorted.slice(0, 5);
    },
    
    // Separar cursos de mención (hasMention = true) para la columna derecha
    groupedRightMaterias() {
      const materiasProcessed = this.separateCoursesByMention();
      const filtered = materiasProcessed.mention.filter(materia => 
        materia.allSchedules && materia.allSchedules.length > 0
      );
      
      // Agregar Ciencias Sociales a la columna derecha si existe en la columna izquierda
      const cienciasSocialesFromLeft = materiasProcessed.common.find(materia => 
        (materia.subjectName === 'Ciencias Sociales' || materia.displayName === 'Ciencias Sociales') &&
        materia.allSchedules && materia.allSchedules.length > 0
      );
      
      // Combinar cursos de mención con Ciencias Sociales si existe
      const combinedMaterias = [...filtered];
      if (cienciasSocialesFromLeft) {
        combinedMaterias.unshift(cienciasSocialesFromLeft); // Agregar al inicio
      }
      
      // Aplicar filtro especial para Matemática Electivo
      const filteredWithDependencies = combinedMaterias.filter(materia => {
        // Si es Matemática Electivo, verificar que haya cupos en Matemática tradicional
        if (this.isMatematicaElectivo(materia.subjectName)) {
          // Buscar Matemática tradicional en las materias procesadas de la columna izquierda
          const materiasProcessed = this.separateCoursesByMention();
          const matematicaTradicional = materiasProcessed.common.find(m => 
            this.isMatematicaTradicional(m.subjectName)
          );
          return matematicaTradicional && this.materiaHasSchedules(matematicaTradicional);
        }
        return true; // Para otras materias, no aplicar filtro
      });
      
      const sorted = this.sortCoursesByCustomOrder(filteredWithDependencies, 'mention');
      // Aplicar regla especial: Ciencias Sociales antes que Lenguaje
      const reordered = this.applyCienciasSocialesRule(sorted);
      // Limitar a máximo 5 cursos por columna
      return reordered.slice(0, 5);
    },

    

    // Obtener las clases de los time slots
    timeSlotClasses() {
      return (courseTimeSlot) => {
        const isMulti = courseTimeSlot.displayText.includes('<br>');
        const isSelected = courseTimeSlot.allSchedules.some(s => this.isScheduleSelected(s.courseId, s.id));
        const hasConflict = courseTimeSlot.allSchedules.some(s => this.hasScheduleConflict(s.courseId, s.id) && !this.isScheduleSelected(s.courseId, s.id));
        
        return {
          'multi-time-slot': isMulti,
          'single-time-slot': !isMulti,
          'active': isSelected,
          'selected': isSelected,
          'conflict': hasConflict
        };
      };
    }
  },
  watch: {
    selectedCourses: {
      handler(newVal, oldVal) {
        // Liberar colores de cursos que ya no están seleccionados
        if (oldVal && oldVal.length > 0) {
          const newCourseIds = new Set(newVal.map(c => `${c.courseId}-${this.getSubjectNameByCourseId(c.courseId)}`));
          oldVal.forEach(oldCourse => {
            const oldKey = `${oldCourse.courseId}-${this.getSubjectNameByCourseId(oldCourse.courseId)}`;
            if (!newCourseIds.has(oldKey)) {
              // Liberar el color del curso deseleccionado
              const course = this.findCourseById(oldCourse.courseId);
              if (course) {
                const subjectName = course.subjectName || course.name || '';
                this.releaseCourseColor(oldCourse.courseId, subjectName);
              }
            }
          });
        }
        
        // Sincronizar el prop con el estado local
        this.localSelectedCourses = [...newVal];
      },
      immediate: true
    },
    
    // Watcher para validar si se puede continuar
    localSelectedCourses: {
      handler() {
        const canContinue = this.validateCanContinue();
        this.$emit('validation-changed', canContinue);
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    // Métodos auxiliares para comparaciones exactas de nombres de cursos
    isMatematicaElectivo(subjectName) {
      return subjectName === this.COURSE_NAMES.MATEMATICA_ELECTIVO || 
             subjectName === 'Matemática (M2) Electivo' ||
             subjectName === 'Matemática Electivo';
    },
    
    isMatematicaTradicional(subjectName) {
      return subjectName === this.COURSE_NAMES.MATEMATICA ||
             subjectName === 'Matemática (M1)' ||
             subjectName === 'Matemática';
    },
    
    // Helper: aplica exclusividad de menciones y deselecciona comunes correspondientes
    applyMentionExclusivity(selectedCourseName) {
      const mentionCourses = ['Biología Mención', 'Física Mención', 'Química Mención'];
      const mentionToCommonMap = {
        'Biología Mención': 'Biología',
        'Física Mención': 'Física',
        'Química Mención': 'Química'
      };
      
      // Si el curso seleccionado no es una mención, no hacer nada
      if (!mentionCourses.includes(selectedCourseName)) {
        return;
      }
      
      // 1) Deseleccionar otras menciones
      const otherMentionsToDeselect = this.localSelectedCourses.filter(sel => {
        const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
        return mentionCourses.includes(selCourseName) && selCourseName !== selectedCourseName;
      });
      otherMentionsToDeselect.forEach(sel => {
        const course = this.findCourseById(sel.courseId);
        if (course) {
          const subjectName = course.subjectName || course.name || '';
          this.releaseCourseColor(sel.courseId, subjectName);
        }
      });
      this.localSelectedCourses = this.localSelectedCourses.filter(sel => {
        const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
        return !mentionCourses.includes(selCourseName) || selCourseName === selectedCourseName;
      });
      
      // 2) Deseleccionar el común correspondiente a la mención seleccionada
      const correspondingCommon = mentionToCommonMap[selectedCourseName];
      if (correspondingCommon) {
        const commonCoursesToDeselect = this.localSelectedCourses.filter(sel => {
          const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
          return selCourseName === correspondingCommon;
        });
        commonCoursesToDeselect.forEach(sel => {
          const course = this.findCourseById(sel.courseId);
          if (course) {
            const subjectName = course.subjectName || course.name || '';
            this.releaseCourseColor(sel.courseId, subjectName);
          }
        });
        this.localSelectedCourses = this.localSelectedCourses.filter(sel => {
          const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
          return selCourseName !== correspondingCommon;
        });
      }
    },
    
    isMatematica(subjectName) {
      return subjectName === this.COURSE_NAMES.MATEMATICA || subjectName === this.COURSE_NAMES.MATEMATICA_ELECTIVO;
    },
    
    // Método para ordenar cursos según el orden personalizado
    sortCoursesByCustomOrder(materias, type) {
      const commonOrder = [
        'Matemática (M1)',
        'Competencia Lectora', 
        'Biología',
        'Física',
        'Química'
      ];
      
      const mentionOrder = [
        'Matemática (M2) Electivo',
        'Ciencias Sociales',
        'Biología Mención',
        'Física Mención', 
        'Química Mención'
      ];
      
      const orderArray = type === 'common' ? commonOrder : mentionOrder;
      
      return materias.sort((a, b) => {
        const indexA = orderArray.findIndex(item => 
          a.subjectName === item || a.displayName === item ||
          (item === 'Competencia Lectora' && (a.subjectName === 'Lenguaje' || a.displayName === 'Lenguaje')) ||
          (item === 'Matemática (M1)' && (a.subjectName === 'Matemática' || a.displayName === 'Matemática')) ||
          (item === 'Matemática (M2) Electivo' && (a.subjectName === 'Matemática Electivo' || a.displayName === 'Matemática Electivo'))
        );
        const indexB = orderArray.findIndex(item => 
          b.subjectName === item || b.displayName === item ||
          (item === 'Competencia Lectora' && (b.subjectName === 'Lenguaje' || b.displayName === 'Lenguaje')) ||
          (item === 'Matemática (M1)' && (b.subjectName === 'Matemática' || b.displayName === 'Matemática')) ||
          (item === 'Matemática (M2) Electivo' && (b.subjectName === 'Matemática Electivo' || b.displayName === 'Matemática Electivo'))
        );
        
        // Si ambos están en el array, ordenar por posición
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        
        // Si solo uno está en el array, ponerlo primero
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        
        // Si ninguno está en el array, mantener orden alfabético
        return a.subjectName.localeCompare(b.subjectName);
      });
    },

    // Método para aplicar la regla especial: Ciencias Sociales antes que Lenguaje
    applyCienciasSocialesRule(materias) {
      
      const cienciasSociales = materias.filter(m => 
        m.subjectName === 'Ciencias Sociales' || m.displayName === 'Ciencias Sociales'
      );
      const competenciaLectora = materias.filter(m => 
        m.subjectName === 'Competencia Lectora' || m.displayName === 'Competencia Lectora' ||
        m.subjectName === 'Lenguaje' || m.displayName === 'Lenguaje'
      );
      const otros = materias.filter(m => 
        m.subjectName !== 'Ciencias Sociales' && 
        m.subjectName !== 'Competencia Lectora' &&
        m.subjectName !== 'Lenguaje' &&
        m.displayName !== 'Ciencias Sociales' &&
        m.displayName !== 'Competencia Lectora' &&
        m.displayName !== 'Lenguaje'
      );
      
      
      // Si hay Ciencias Sociales y Competencia Lectora, poner Ciencias Sociales primero
      if (cienciasSociales.length > 0 && competenciaLectora.length > 0) {
        const resultado = [...cienciasSociales, ...competenciaLectora, ...otros];
        return resultado;
      }
      
      // Si no hay ambos, mantener el orden original
      return materias;
    },

    // Método para separar cursos por mención
    separateCoursesByMention() {
      const groupedMap = {};
      
      // Procesar todos los cursos directamente del JSON
      this.allMaterias.forEach(materia => {
        if (materia.courses && Array.isArray(materia.courses)) {
          materia.courses.forEach(course => {
            const subjectName = course.Subject?.name || materia.subjectName;
            const isMention = course.hasMention;
            
            // Crear clave única para cada combinación de materia + tipo (común/mención)
            const key = `${subjectName}_${isMention ? 'mention' : 'common'}`;
            
            if (!groupedMap[key]) {
              groupedMap[key] = {
                subjectName: subjectName,
                displayName: formatSubjectName(subjectName),
                subjectId: course.SubjectId || materia.subjectId,
                hasMention: isMention,
                courses: [],
                allSchedules: [],
                totalCourses: 0,
                availableSeats: 0,
                totalSeats: 0
              };
            }
            
            // Agregar el curso
            groupedMap[key].courses.push(course);
            groupedMap[key].totalCourses++;
            groupedMap[key].availableSeats += (course.seatsAvailable || 0);
            groupedMap[key].totalSeats += (course.seatsCapacity || 0);
            
            // Agregar horarios
            if (course.Schedules && Array.isArray(course.Schedules)) {
              course.Schedules.forEach(schedule => {
                groupedMap[key].allSchedules.push({
                  ...schedule,
                  courseId: course.id,
                  courseName: course.name,
                  subjectName: subjectName
                });
              });
            }
          });
        }
      });
      
      // Separar en comunes y menciones
      const allGrouped = Object.values(groupedMap);
      const common = allGrouped.filter(materia => !materia.hasMention);
      const mention = allGrouped.filter(materia => materia.hasMention);
      
      return { common, mention };
    },
    // Método para agrupar materias por nombre de asignatura
    groupMateriasBySubjectName(materias) {
      if (!materias || !Array.isArray(materias)) return [];
      
      const groupedMap = {};
      
      materias.forEach(materia => {
        const subjectName = materia.subjectName;
        
        // Crear una sola entrada por materia (no por curso)
        if (!groupedMap[subjectName]) {
          groupedMap[subjectName] = {
            subjectName: subjectName,
            displayName: formatSubjectName(subjectName),
            subjectId: materia.subjectId,
            hasMention: materia.hasMention,
            courses: [], // Todos los cursos de esta materia
            allSchedules: [], // Todos los horarios de todos los cursos
            totalCourses: 0,
            availableSeats: 0,
            totalSeats: 0
          };
        }
        
        // Agregar todos los cursos de esta materia
        if (materia.courses && Array.isArray(materia.courses)) {
          materia.courses.forEach(course => {
            // Solo agregar el curso si no está ya en la lista
            const existingCourse = groupedMap[subjectName].courses.find(c => c.id === course.id);
            if (!existingCourse) {
              groupedMap[subjectName].courses.push(course);
              groupedMap[subjectName].totalCourses++;
              
              // Acumular cupos disponibles
              groupedMap[subjectName].availableSeats += (course.seatsAvailable || 0);
              groupedMap[subjectName].totalSeats += (course.seatsCapacity || 0);
              
              // Agregar todos los horarios de este curso
              if (course.Schedules && Array.isArray(course.Schedules)) {
                course.Schedules.forEach(schedule => {
                  groupedMap[subjectName].allSchedules.push({
                    ...schedule,
                    courseId: course.id,
                    courseName: course.name,
                    subjectName: subjectName
                  });
                });
              }
            }
          });
        }
      });
      
      return Object.values(groupedMap);
    },
    
    // Métodos de expansión de cursos
    toggleCourseExpansion(uniqueKey) {

      
      const index = this.expandedCourses.indexOf(uniqueKey);
      if (index > -1) {
        // Cerrar el curso - NO deseleccionar horarios
        this.expandedCourses.splice(index, 1);
        // Comentado: this.deselectAllSchedulesForUniqueKey(uniqueKey);
      } else {
        // Abrir el curso
        this.expandedCourses.push(uniqueKey);
      }
      
      // Forzar la actualización de la vista
      this.$forceUpdate();
    },
    
    isCourseExpanded(uniqueKey) {
      const isExpanded = this.expandedCourses.includes(uniqueKey);
      return isExpanded;
    },
    
    getButtonStyle(uniqueKey) {
      // Extraer el courseId de la clave única
      const courseId = uniqueKey.split('_')[1];
      
      // Verificar si hay horarios seleccionados para este curso
      const hasSelectedSchedules = this.localSelectedCourses.some(sel => 
        String(sel.courseId) === courseId
      );
      
      if (this.isCourseExpanded(uniqueKey) || hasSelectedSchedules) {
        // Usar azul si está expandido O si tiene horarios seleccionados
        return { backgroundColor: '#3691D8' };
      } else {
        // Si está cerrado y no tiene horarios seleccionados, no aplicar color de fondo
        return {};
      }
    },
    
    // Métodos de agrupación de horarios
    getDayGroups(schedules) {
      if (!schedules || !Array.isArray(schedules)) return [];
      
      // Primero, agrupar por curso y días (usando la clave original)
      const courseGroups = {};
      
      schedules.forEach(schedule => {
        const dayKey = this.getDayKeyForSchedule(schedule, schedules);
        
        if (!courseGroups[dayKey]) {
          courseGroups[dayKey] = {
            days: [],
            schedules: [],
            timeSlots: new Set(),
            courseId: schedule.courseId
          };
        }
        
        // Agregar el horario al grupo
        courseGroups[dayKey].schedules.push(schedule);
        
        // Agregar el día si no existe ya
        if (!courseGroups[dayKey].days.includes(schedule.dayOfWeek)) {
          courseGroups[dayKey].days.push(schedule.dayOfWeek);
        }
        
        // Agregar el slot de tiempo único
        const timeSlot = `${schedule.startTime}-${schedule.endTime}`;
        courseGroups[dayKey].timeSlots.add(timeSlot);
      });
      
      // Ahora, agrupar los grupos de curso por días únicos
      const dayGroups = {};
      
      Object.values(courseGroups).forEach(courseGroup => {
        // Ordenar los días
        courseGroup.days.sort((a, b) => {
          const dayOrder = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
          return dayOrder.indexOf(a) - dayOrder.indexOf(b);
        });
        
        const daysKey = courseGroup.days.join('-');
        
        if (!dayGroups[daysKey]) {
          dayGroups[daysKey] = {
            days: courseGroup.days,
            schedules: [],
            timeSlots: [], // Cambiar a array para mantener duplicados por curso
            courseIds: new Set()
          };
        }
        
        // Agregar todos los horarios de este curso
        dayGroups[daysKey].schedules.push(...courseGroup.schedules);
        
        // Agregar todos los timeSlots de este curso (mantener duplicados por curso)
        courseGroup.timeSlots.forEach(timeSlot => {
          dayGroups[daysKey].timeSlots.push({
            timeSlot: timeSlot,
            courseId: courseGroup.courseId
          });
        });
        
        // Agregar el courseId
        dayGroups[daysKey].courseIds.add(courseGroup.courseId);
      });
      
      // Convertir a array y procesar
      const result = Object.values(dayGroups).map(group => {
        // Crear un solo timeSlot que represente todos los horarios del curso
        // Agrupar por courseId y crear un objeto que contenga todos los horarios
        const courseTimeSlots = {};
        
        group.timeSlots.forEach(timeSlotData => {
          if (!courseTimeSlots[timeSlotData.courseId]) {
            courseTimeSlots[timeSlotData.courseId] = {
              courseId: timeSlotData.courseId,
              timeSlots: new Set(),
              allSchedules: []
            };
          }
          courseTimeSlots[timeSlotData.courseId].timeSlots.add(timeSlotData.timeSlot);
        });
        
        // Agregar todos los horarios del curso
        group.schedules.forEach(schedule => {
          if (courseTimeSlots[schedule.courseId]) {
            courseTimeSlots[schedule.courseId].allSchedules.push(schedule);
          }
        });
        
        // Convertir a array y ordenar
        const sortedCourseTimeSlots = Object.values(courseTimeSlots).map(courseData => ({
          courseId: courseData.courseId,
          timeSlots: Array.from(courseData.timeSlots).sort(),
          allSchedules: courseData.allSchedules,
          // Crear un texto descriptivo para mostrar todos los horarios
          displayText: this.createMultiTimeDisplayText(courseData.allSchedules),
          // Agregar subjectName para consistencia de colores
          subjectName: courseData.allSchedules[0]?.subjectName || ''
        }));
        
        return {
          days: group.days,
          schedules: group.schedules,
          courseTimeSlots: sortedCourseTimeSlots, // Nueva estructura
          courseIds: Array.from(group.courseIds),
          // Usar el primer horario como representativo del grupo
          representativeSchedule: group.schedules[0]
        };
      });
      
      // Ordenar por días (Lunes primero, etc.)
      result.sort((a, b) => {
        const dayOrder = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
        const aFirstDay = dayOrder.indexOf(a.days[0]);
        const bFirstDay = dayOrder.indexOf(b.days[0]);
        
        return aFirstDay - bFirstDay;
      });
      
      return result;
    },
    
    // Método auxiliar para determinar la clave de días para un horario
    getDayKeyForSchedule(schedule, allSchedules) {
      // Buscar todos los horarios del MISMO CURSO
      // Agrupar TODOS los horarios del mismo curso, sin importar el tiempo
      const sameCourseSchedules = allSchedules.filter(s => 
        s.courseId === schedule.courseId
      );
      
      // Crear una clave basada en TODOS los días únicos de este curso
      const uniqueDays = [...new Set(sameCourseSchedules.map(s => s.dayOfWeek))];
      uniqueDays.sort((a, b) => {
        const dayOrder = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
        return dayOrder.indexOf(a) - dayOrder.indexOf(b);
      });
      
      // La clave es TODOS los días del curso + courseId
      return `${uniqueDays.join('-')}_${schedule.courseId}`;
    },
    
    // Método para formatear un slot de tiempo
    formatTimeSlot(timeSlot) {
      if (!timeSlot) return '';
      const [startTime, endTime] = timeSlot.split('-');
      return `${this.formatTime(startTime)} - ${this.formatTime(endTime)}`;
    },
    
    // Método para crear texto descriptivo de múltiples horarios
    createMultiTimeDisplayText(schedules) {
      if (!schedules || schedules.length === 0) return '';
      
      // Agrupar horarios por día
      const dayGroups = {};
      schedules.forEach(schedule => {
        if (!dayGroups[schedule.dayOfWeek]) {
          dayGroups[schedule.dayOfWeek] = [];
        }
        dayGroups[schedule.dayOfWeek].push(schedule);
      });
      
      // Verificar si todos los horarios son iguales
      const allTimes = schedules.map(s => `${s.startTime}-${s.endTime}`);
      const uniqueTimes = [...new Set(allTimes)];
      const allTimesAreEqual = uniqueTimes.length === 1;
      
      if (allTimesAreEqual) {
        // Si todos los horarios son iguales, solo mostrar la hora
        const timeText = `${this.formatTime(schedules[0].startTime)} - ${this.formatTime(schedules[0].endTime)}`;
        return timeText;
      } else {
        // Si los horarios son diferentes, mostrar "Día: horario | Día: horario"
        const dayTexts = Object.keys(dayGroups).sort((a, b) => {
          const dayOrder = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
          return dayOrder.indexOf(a) - dayOrder.indexOf(b);
        }).map(day => {
          const daySchedules = dayGroups[day];
          const timeText = daySchedules.map(s => 
            `${this.formatTime(s.startTime)} - ${this.formatTime(s.endTime)}`
          ).join(', ');
          return `${day}: ${timeText}`;
        });
        
        return dayTexts.join('<br>');
      }
    },
    
    // Método para manejar la selección de un slot de tiempo específico
    toggleTimeSlot(dayGroup, timeSlot) {
      // Encontrar todos los horarios que coinciden con este timeSlot
      const matchingSchedules = dayGroup.schedules.filter(s => 
        `${s.startTime}-${s.endTime}` === timeSlot
      );
      
      if (matchingSchedules.length === 0) return;
      
      // Verificar si algún horario de este timeSlot está seleccionado
      const isTimeSlotSelected = matchingSchedules.some(s => this.isScheduleSelected(s.courseId, s.id));
      
      if (isTimeSlotSelected) {
        // Deseleccionar todos los horarios de este timeSlot
        matchingSchedules.forEach(schedule => {
          const cIdStr = String(schedule.courseId);
          const sIdStr = String(schedule.id);
          this.localSelectedCourses = this.localSelectedCourses.filter(sel => 
            !(String(sel.courseId) === cIdStr && String(sel.scheduleId) === sIdStr)
          );
        });
      } else {
        // Verificar conflictos en cualquiera de los horarios del timeSlot
        let conflict = null;
        let conflictSchedule = null;
        for (const sch of matchingSchedules) {
          const cIdStr = String(sch.courseId);
          const sIdStr = String(sch.id);
          const c = this.checkScheduleConflict(cIdStr, sIdStr);
          if (c) {
            conflict = c;
            conflictSchedule = sch;
            break;
          }
        }
        if (conflict) {
          // Mostrar modal de conflicto
          this.showConflictModal = true;
          this.conflictData = conflict;
          this.pendingScheduleSelection = { 
            courseId: String(conflictSchedule.courseId), 
            scheduleId: String(conflictSchedule.id), 
            isTimeSlot: true, 
            dayGroup: dayGroup, 
            timeSlot: timeSlot,
            matchingSchedules: matchingSchedules 
          };
          return;
        }
        
        // IMPORTANTE: Deseleccionar TODOS los horarios previos de TODAS las materias
        // que están en este grupo de días, ya que ahora agrupamos por días únicos
        const allCourseIdsInGroup = dayGroup.courseIds;
        this.localSelectedCourses = this.localSelectedCourses.filter(sel => {
          return !allCourseIdsInGroup.includes(Number(sel.courseId));
        });
        
        // Seleccionar todos los horarios del timeSlot
        matchingSchedules.forEach(schedule => {
          this.localSelectedCourses.push({ 
            courseId: String(schedule.courseId), 
            scheduleId: String(schedule.id) 
          });
        });
      }
      
      // Emitir el evento para el componente padre
      this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
      
      // Forzar la actualización de la vista
      this.$forceUpdate();
    },
    
    // Método para verificar si quedan últimos cupos para un timeSlot específico
    isLastSeatsForTimeSlot(dayGroup, timeSlot) {
      // Buscar un horario representativo de este timeSlot
      const representativeSchedule = dayGroup.schedules.find(s => 
        `${s.startTime}-${s.endTime}` === timeSlot
      );
      
      if (!representativeSchedule) return false;
      
      return this.isLastSeats(representativeSchedule);
    },
    
    // Método para manejar la selección de un slot de tiempo específico por curso
    toggleTimeSlotByCourse(dayGroup, timeSlotData) {
      // Encontrar todos los horarios que coinciden con este timeSlot y courseId
      const matchingSchedules = dayGroup.schedules.filter(s => 
        `${s.startTime}-${s.endTime}` === timeSlotData.timeSlot &&
        s.courseId === timeSlotData.courseId
      );
      
      if (matchingSchedules.length === 0) return;
      
      // Verificar si algún horario de este timeSlot y curso está seleccionado
      const isTimeSlotSelected = matchingSchedules.some(s => this.isScheduleSelected(s.courseId, s.id));
      
      if (isTimeSlotSelected) {
        // Deseleccionar todos los horarios de este timeSlot y curso
        matchingSchedules.forEach(schedule => {
          const cIdStr = String(schedule.courseId);
          const sIdStr = String(schedule.id);
          this.localSelectedCourses = this.localSelectedCourses.filter(sel => 
            !(String(sel.courseId) === cIdStr && String(sel.scheduleId) === sIdStr)
          );
        });
      } else {
        // Verificar conflictos en cualquiera de los horarios del timeSlot por curso
        let conflict = null;
        let conflictSchedule = null;
        for (const sch of matchingSchedules) {
          const cIdStr = String(sch.courseId);
          const sIdStr = String(sch.id);
          const c = this.checkScheduleConflict(cIdStr, sIdStr);
          if (c) {
            conflict = c;
            conflictSchedule = sch;
            break;
          }
        }
        if (conflict) {
          // Mostrar modal de conflicto
          this.showConflictModal = true;
          this.conflictData = conflict;
          this.pendingScheduleSelection = { 
            courseId: String(conflictSchedule.courseId), 
            scheduleId: String(conflictSchedule.id), 
            isTimeSlot: true, 
            dayGroup: dayGroup, 
            timeSlot: timeSlotData.timeSlot,
            matchingSchedules: matchingSchedules 
          };
          return;
        }
        
        // IMPORTANTE: Deseleccionar TODOS los horarios previos de la misma materia
        const subjectName = this.getSubjectNameByCourseId(courseIdStr);
        this.localSelectedCourses = this.localSelectedCourses.filter(sel => {
          const selSubjectName = this.getSubjectNameByCourseId(String(sel.courseId));
          return selSubjectName !== subjectName;
        });
        
        // Seleccionar todos los horarios del timeSlot y curso específico
        matchingSchedules.forEach(schedule => {
          this.localSelectedCourses.push({ 
            courseId: String(schedule.courseId), 
            scheduleId: String(schedule.id) 
          });
        });
      }
      
      // Emitir el evento para el componente padre
      this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
      
      // Forzar la actualización de la vista
      this.$forceUpdate();
    },
    
    // Método para verificar si quedan últimos cupos para un timeSlot específico por curso
    isLastSeatsForTimeSlotByCourse(dayGroup, timeSlotData) {
      // Buscar un horario representativo de este timeSlot y curso
      const representativeSchedule = dayGroup.schedules.find(s => 
        `${s.startTime}-${s.endTime}` === timeSlotData.timeSlot &&
        s.courseId === timeSlotData.courseId
      );
      
      if (!representativeSchedule) return false;
      
      return this.isLastSeats(representativeSchedule);
    },
    
    // Método para manejar la selección de un curso con múltiples horarios
    toggleCourseTimeSlot(dayGroup, courseTimeSlot) {
      // Verificar si algún horario de este curso está seleccionado
      const isCourseSelected = courseTimeSlot.allSchedules.some(s => this.isScheduleSelected(s.courseId, s.id));
      
      if (isCourseSelected) {
        // Verificar si se está deseleccionando Matemática tradicional
        const representativeSchedule = courseTimeSlot.allSchedules[0];
        const courseName = this.getSubjectNameByCourseId(String(representativeSchedule.courseId));
        
        if (this.isMatematicaTradicional(courseName)) {
          // Deseleccionar automáticamente Matemática Electivo si está seleccionada
          this.localSelectedCourses = this.localSelectedCourses.filter(sel => {
            const selSubjectName = this.getSubjectNameByCourseId(String(sel.courseId));
            return !this.isMatematicaElectivo(selSubjectName);
          });
        }
        
        // Deseleccionar todos los horarios de este curso
        courseTimeSlot.allSchedules.forEach(schedule => {
          const cIdStr = String(schedule.courseId);
          const sIdStr = String(schedule.id);
          this.localSelectedCourses = this.localSelectedCourses.filter(sel => 
            !(String(sel.courseId) === cIdStr && String(sel.scheduleId) === sIdStr)
          );
        });
      } else {
        // Verificar conflictos usando el primer horario como representativo
        const representativeSchedule = courseTimeSlot.allSchedules[0];
        const courseIdStr = String(representativeSchedule.courseId);
        const scheduleIdStr = String(representativeSchedule.id);
        
        // Usar el método de grupo para seleccionar TODOS los horarios del timeSlot
        const allScheduleIds = courseTimeSlot.allSchedules.map(s => s.id);
        // Configurar pendingScheduleSelection para el modal si hay conflicto
        this.pendingScheduleSelection = { 
          courseId: courseIdStr, 
          scheduleId: scheduleIdStr, 
          isCourseTimeSlot: true, 
          dayGroup: dayGroup, 
          courseTimeSlot: courseTimeSlot,
          matchingSchedules: courseTimeSlot.allSchedules
        };
        
        // Usar el método de grupo para seleccionar todos los horarios
        const success = this.selectScheduleGroupForCourse(courseIdStr, allScheduleIds);
        
        if (!success) {
          // Si no se pudo seleccionar por conflicto, el modal ya se mostró
          return;
        }
      }
      
      // Emitir el evento para el componente padre
      this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
      
      // Forzar la actualización de la vista
      this.$forceUpdate();
    },
    
    // Método para verificar si quedan últimos cupos para un curso con múltiples horarios
    isLastSeatsForCourseTimeSlot(courseTimeSlot) {
      // Buscar un horario representativo de este curso
      const representativeSchedule = courseTimeSlot.allSchedules[0];
      
      if (!representativeSchedule) return false;
      
      return this.isLastSeats(representativeSchedule);
    },
    

    

    
    getUniqueSchedules(schedules) {
      if (!schedules || schedules.length === 0) return [];
      
      // Crear un mapa para eliminar duplicados basados en hora de inicio, fin Y día
      const uniqueMap = new Map();
      
      schedules.forEach(schedule => {
        const timeKey = `${schedule.startTime}-${schedule.endTime}-${schedule.dayOfWeek}`;
        if (!uniqueMap.has(timeKey)) {
          uniqueMap.set(timeKey, schedule);
        }
      });
      
      return Array.from(uniqueMap.values());
    },
    
    formatDayGroup(days) {
      if (!days || !Array.isArray(days) || days.length === 0) return '';
      
      // Si solo hay un día, mostrarlo individualmente
      if (days.length === 1) {
        const day = days[0];
        const dayNames = {
          'Lunes': 'Lunes',
          'Martes': 'Martes', 
          'Miércoles': 'Miércoles',
          'Jueves': 'Jueves',
          'Viernes': 'Viernes',
          'Sábado': 'Sábado',
          'Domingo': 'Domingo'
        };
        return dayNames[day] || day;
      }
      
      // Si hay múltiples días, agruparlos con "y"
      const dayNames = {
        'Lunes': 'Lunes',
        'Martes': 'Martes', 
        'Miércoles': 'Miércoles',
        'Jueves': 'Jueves',
        'Viernes': 'Viernes',
        'Sábado': 'Sábado',
        'Domingo': 'Domingo'
      };
      
      const formattedDays = days.map(day => dayNames[day] || day);
      
      if (formattedDays.length === 2) {
        return `${formattedDays[0]} y ${formattedDays[1]}`;
      } else {
        // Para más de 2 días: "Lunes, Martes y Miércoles"
        const lastDay = formattedDays.pop();
        return `${formattedDays.join(', ')} y ${lastDay}`;
      }
    },
    
    // Métodos de selección de horarios
    isScheduleSelected(courseId, scheduleId) {
      // Convertir a string para comparación más robusta
      const courseIdStr = String(courseId);
      const scheduleIdStr = String(scheduleId);
      
      // Verificar si este horario específico está seleccionado
      const isSelected = this.localSelectedCourses.some(sel => 
        String(sel.courseId) === courseIdStr && String(sel.scheduleId) === scheduleIdStr
      );
      
      return isSelected;
    },
    
    toggleScheduleGroup(dayGroup) {
      // Al hacer clic en un grupo de días, usar el primer horario como representativo
      // pero seleccionar/deseleccionar todos los horarios del grupo
      const representativeSchedule = dayGroup.representativeSchedule;
      const courseIdStr = String(representativeSchedule.courseId);
      const scheduleIdStr = String(representativeSchedule.id);
      
      // Verificar si algún horario del grupo está seleccionado
      const isGroupSelected = dayGroup.schedules.some(s => this.isScheduleSelected(s.courseId, s.id));
      
      if (isGroupSelected) {
        // Si ya está seleccionado, deseleccionar todos los horarios del grupo
        dayGroup.schedules.forEach(schedule => {
          const cIdStr = String(schedule.courseId);
          const sIdStr = String(schedule.id);
          this.localSelectedCourses = this.localSelectedCourses.filter(sel => 
            !(String(sel.courseId) === cIdStr && String(sel.scheduleId) === sIdStr)
          );
        });
      } else {
        // Verificar conflictos usando el horario representativo
        const conflict = this.checkScheduleConflict(courseIdStr, scheduleIdStr);
        if (conflict) {
          // Mostrar modal de conflicto
          this.showConflictModal = true;
          this.conflictData = conflict;
          this.pendingScheduleSelection = { courseId: courseIdStr, scheduleId: scheduleIdStr, isGroup: true, dayGroup: dayGroup };
          return;
        }
        
        // Deseleccionar TODOS los horarios previos de la misma materia (regla: máximo un horario por sección)
        const subjectName = this.getSubjectNameByCourseId(courseIdStr);
        
        this.localSelectedCourses = this.localSelectedCourses.filter(sel => {
          const selSubjectName = this.getSubjectNameByCourseId(String(sel.courseId));
          const shouldKeep = selSubjectName !== subjectName;
          return shouldKeep;
        });
        
        // Seleccionar todos los horarios del grupo
        dayGroup.schedules.forEach(schedule => {
          this.localSelectedCourses.push({ 
            courseId: String(schedule.courseId), 
            scheduleId: String(schedule.id) 
          });
        });
      }
      
      // Emitir el evento para el componente padre
      this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
      
      // Forzar la actualización de la vista
      this.$forceUpdate();
    },
    
    // Método centralizado para seleccionar horario
    selectScheduleForCourse(courseId, scheduleId, skipValidation = false) {
      const courseIdStr = String(courseId);
      const scheduleIdStr = String(scheduleId);
      const courseName = this.getSubjectNameByCourseId(courseIdStr);
      
      // VALIDACIÓN: Matemática Electivo requiere Matemática tradicional (solo si no se omite)
      if (!skipValidation && this.isMatematicaElectivo(courseName)) {
        const hasMatematicaTradicional = this.localSelectedCourses.some(sel => {
          const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
          return this.isMatematicaTradicional(selCourseName);
        });
        
        if (!hasMatematicaTradicional) {
          // No mostrar alerta, simplemente no permitir la selección
          return false;
        }
      }
      
      // VALIDACIÓN: Curso común bloqueado por tener mención seleccionada
      if (this.isCommonCourseBlocked(courseIdStr)) {
        return false;
      }
      
      // PASO 1: Deseleccionar TODOS los horarios del mismo NOMBRE DE CURSO
      this.localSelectedCourses = this.localSelectedCourses.filter(sel => {
        const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
        return selCourseName !== courseName;
      });
      
      // PASO 1.5: Aplicar exclusividad de menciones y deseleccionar comunes
      this.applyMentionExclusivity(courseName);
      
      // PASO 2: Verificar conflictos con otros cursos (solo si no se omite)
      if (!skipValidation) {
        const conflict = this.checkScheduleConflict(courseIdStr, scheduleIdStr);
        if (conflict) {
          this.showConflictModal = true;
          this.conflictData = conflict;
          this.pendingScheduleSelection = { courseId: courseIdStr, scheduleId: scheduleIdStr };
          return false; // No se seleccionó por conflicto
        }
      }
      
      // PASO 3: Seleccionar el nuevo horario
      this.localSelectedCourses.push({ 
        courseId: courseIdStr, 
        scheduleId: scheduleIdStr 
      });
      
      // Validar que solo hay 1 horario por nombre de curso
      if (!this.validateSingleSchedulePerCourseName()) {
        this.cleanupDuplicateSchedulesByCourseName();
      }
      
      // Emitir evento y actualizar vista
      this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
      this.$forceUpdate();
      
      return true; // Se seleccionó correctamente
    },

    // Método para seleccionar múltiples horarios de un grupo de días
    selectScheduleGroupForCourse(courseId, scheduleIds, skipValidation = false) {
      const courseIdStr = String(courseId);
      const courseName = this.getSubjectNameByCourseId(courseIdStr);
      
      // VALIDACIÓN ESPECIAL: Matemática Electivo requiere Matemática tradicional (solo si no se omite)
      if (!skipValidation && this.isMatematicaElectivo(courseName)) {
        const hasMatematicaTradicional = this.localSelectedCourses.some(sel => {
          const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
          return this.isMatematicaTradicional(selCourseName);
        });
        
        if (!hasMatematicaTradicional) {
          // No mostrar alerta, simplemente no permitir la selección
          return false;
        }
      }
      
      // VALIDACIÓN: Curso común bloqueado por tener mención seleccionada
      if (this.isCommonCourseBlocked(courseIdStr)) {
        return false;
      }
      
      // PASO 1: Deseleccionar TODOS los horarios del mismo NOMBRE DE CURSO
      this.localSelectedCourses = this.localSelectedCourses.filter(sel => {
        const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
        return selCourseName !== courseName;
      });
      
      // PASO 1.5: Aplicar exclusividad de menciones y deseleccionar comunes
      this.applyMentionExclusivity(courseName);
      
      // PASO 2: Verificar conflictos con CUALQUIER horario del grupo (solo si no se omite)
      if (!skipValidation) {
        let conflict = null;
        let conflictingScheduleId = null;
        for (const sid of scheduleIds) {
          const c = this.checkScheduleConflict(courseIdStr, String(sid));
          if (c) {
            conflict = c;
            conflictingScheduleId = String(sid);
            break;
          }
        }
        if (conflict) {
          this.showConflictModal = true;
          this.conflictData = conflict;
          this.pendingScheduleSelection = { 
            courseId: courseIdStr, 
            scheduleId: conflictingScheduleId,
            isGroup: true,
            allScheduleIds: scheduleIds
          };
          return false; // No se seleccionó por conflicto
        }
      }
      
      // PASO 3: Seleccionar TODOS los horarios del grupo
      scheduleIds.forEach((scheduleId) => {
        this.localSelectedCourses.push({ 
          courseId: courseIdStr, 
          scheduleId: String(scheduleId)
        });
      });
      // Emitir evento y actualizar vista
      this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
      this.$forceUpdate();
      
      return true; // Se seleccionó correctamente
    },

    toggleSchedule(courseId, scheduleId) {
      const courseIdStr = String(courseId);
      const scheduleIdStr = String(scheduleId);
      
      // Verificar si ya está seleccionado
      const isSelected = this.localSelectedCourses.some(sel => 
        String(sel.courseId) === courseIdStr && String(sel.scheduleId) === scheduleIdStr
      );
      
      if (isSelected) {
        // VALIDACIÓN: Si se deselecciona Matemática tradicional, también deseleccionar Matemática Electivo
        const subjectName = this.getSubjectNameByCourseId(courseIdStr);
        if (this.isMatematicaTradicional(subjectName)) {
          // Deseleccionar automáticamente Matemática Electivo si está seleccionada
          this.localSelectedCourses = this.localSelectedCourses.filter(sel => {
            const selSubjectName = this.getSubjectNameByCourseId(String(sel.courseId));
            return !this.isMatematicaElectivo(selSubjectName);
          });
        }

        // Liberar el color del curso antes de deseleccionarlo
        const course = this.findCourseById(courseIdStr);
        if (course) {
          const courseSubjectName = course.subjectName || course.name || '';
          this.releaseCourseColor(courseIdStr, courseSubjectName);
        }

        // Si está seleccionado, deseleccionarlo
        this.localSelectedCourses = this.localSelectedCourses.filter(sel => 
          !(String(sel.courseId) === courseIdStr && String(sel.scheduleId) === scheduleIdStr)
        );
        
        this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
        this.$forceUpdate();
      } else {
        // Si no está seleccionado, usar el método centralizado
        this.selectScheduleForCourse(courseId, scheduleId);
      }
    },
    
    getSubjectNameByCourseId(courseId) {
      // Buscar en todas las materias para encontrar el subjectName del courseId
      const allMaterias = [...this.leftColumnMaterias, ...this.rightColumnMaterias];
      
      // Buscar en las materias originales (con estructura courses)
      for (const materia of allMaterias) {
        if (materia.courses) {
          const course = materia.courses.find(c => String(c.id) === String(courseId));
          if (course) {
            return materia.subjectName;
          }
        }
      }
      
      return null;
    },
    
    // Método para formatear nombres de materias (usa utilidad compartida)
    formatSubjectName,
    
    // Método para obtener el nombre formateado completo de un curso
    getFormattedCourseName(materia) {
      if (!materia) return '';
      
      // Obtener el primer curso de la materia para el contexto
      const course = materia.courses && materia.courses[0] ? materia.courses[0] : null;
      
      // Crear objeto curso con etiquetas para formatFullCourseName
      const courseWithLabels = course ? {
        ...course,
        hasMention: materia.hasMention || false,
        isElective: materia.isElective || false,
        isCommon: materia.isCommon || false
      } : {
        hasMention: materia.hasMention || false,
        isElective: materia.isElective || false,
        isCommon: materia.isCommon || false
      };
      
      // Usar utilidad compartida para formatear nombre completo
      const originalSubjectName = materia.subjectName || materia.displayName || '';
      
      // Para Matemática, verificar si es electivo basado en el nombre
      if (originalSubjectName.toLowerCase().includes('matemática') || originalSubjectName.toLowerCase().includes('matematica')) {
        // Verificar si el nombre contiene "Electivo" - si NO contiene, es M1 normal
        const containsElectivo = materia.isElective || 
                                materia.displayName?.toLowerCase().includes('electivo') || 
                                materia.subjectName?.toLowerCase().includes('electivo') ||
                                originalSubjectName.toLowerCase().includes('electivo');
        
        if (containsElectivo) {
          return 'Matemática (M2) Electivo';
        } else {
          // Si NO contiene "Electivo", es M1 normal
          return 'Matemática (M1)';
        }
      }
      
      return formatFullCourseName(originalSubjectName, courseWithLabels);
    },
    
    getSubjectType(displayName) {
      if (!displayName) return null;
      
      if (/MENCIÓN/gi.test(displayName)) {
        return 'MENCIÓN';
      } else if (/ELECTIVO/gi.test(displayName)) {
        return 'ELECTIVO';
      } else if (/COMÚN/gi.test(displayName)) {
        return 'Común';
      }
      return null;
    },

    getSubjectTypeForMateria(materia) {
      if (!materia) return null;
      
      // Verificar primero el displayName de la materia
      let type = this.getSubjectType(materia.displayName);
      if (type) return type;
      
      // Si no se encuentra en displayName, verificar nombres de cursos individuales
      if (materia.courses && Array.isArray(materia.courses)) {
        for (const course of materia.courses) {
          if (course.name) {
            type = this.getSubjectType(course.name);
            if (type) return type;
          }
        }
      }
      
      return null;
    },
    
    validateScheduleSelection(courseId, scheduleId) {
      // Verificar que el curso existe
      const course = this.findCourseById(courseId);
      if (!course) {
        console.error('Curso no encontrado:', courseId);
        return false;
      }
      
      // Verificar que el horario existe
      const schedule = course.Schedules?.find(s => s.id == scheduleId);
      if (!schedule) {
        console.error('Horario no encontrado:', scheduleId);
        return false;
      }
      
      return true;
    },
    
    findCourseById(courseId) {
      // Buscar solo en las materias originales para evitar recursión
      for (const materia of [...this.leftColumnMaterias, ...this.rightColumnMaterias]) {
        if (materia.courses) {
          const course = materia.courses.find(c => c.id == courseId);
          if (course) return course;
        }
      }
      
      return null;
    },
    
    findMateriaByCourseId(courseId) {
      // Buscar solo en las materias originales para evitar recursión
      for (const materia of [...this.leftColumnMaterias, ...this.rightColumnMaterias]) {
        if (materia.courses) {
          const course = materia.courses.find(c => c.id == courseId);
          if (course) return materia;
        }
      }
      
      return null;
    },

    // Método para encontrar una materia por nombre
    findMateriaByName(materiaName) {
      // Usar las materias originales para evitar recursión infinita
      const allMaterias = [...this.leftColumnMaterias, ...this.rightColumnMaterias];
      return allMaterias.find(materia => 
        materia.subjectName === materiaName
      );
    },

    // Método para verificar si una materia tiene horarios disponibles
    materiaHasSchedules(materia) {
      return materia && materia.allSchedules && materia.allSchedules.length > 0;
    },

    // Método para verificar si Matemática Electivo está bloqueado
    isMatematicaElectivoBlocked(courseId) {
      const courseName = this.getSubjectNameByCourseId(String(courseId));
      if (!this.isMatematicaElectivo(courseName)) {
        return false;
      }
      
      // Matemática (M2) Electivo ya no está bloqueada, pero se validará al seleccionar
      return false;
    },

    // Método para verificar si un curso común está bloqueado por tener su mención seleccionada
    isCommonCourseBlocked(courseId) {
      const courseName = this.getSubjectNameByCourseId(String(courseId));
      
      // Verificar si es un curso común que tiene una mención correspondiente
      const commonToMentionMap = {
        'Biología': 'Biología Mención',
        'Física': 'Física Mención', 
        'Química': 'Química Mención'
      };
      
      const correspondingMention = commonToMentionMap[courseName];
      if (!correspondingMention) {
        return false; // No es un curso común que tenga mención
      }
      
      // Verificar si la mención correspondiente está seleccionada
      const hasMentionSelected = this.localSelectedCourses.some(sel => {
        const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
        return selCourseName === correspondingMention;
      });
      
      return hasMentionSelected;
    },

    // Método para obtener el tooltip de Matemática Electivo
    getMatematicaElectivoTooltip(courseId) {
      const courseName = this.getSubjectNameByCourseId(String(courseId));
      if (!this.isMatematicaElectivo(courseName)) {
        return '';
      }
      
      // Ya no se muestra tooltip de bloqueo, se validará al seleccionar
      return '';
    },

    // Método para obtener el tooltip de cursos comunes bloqueados
    getCommonCourseTooltip(courseId) {
      const courseName = this.getSubjectNameByCourseId(String(courseId));
      
      const commonToMentionMap = {
        'Biología': 'Biología Mención',
        'Física': 'Física Mención', 
        'Química': 'Química Mención'
      };
      
      const correspondingMention = commonToMentionMap[courseName];
      if (!correspondingMention) {
        return '';
      }
      
      const hasMentionSelected = this.localSelectedCourses.some(sel => {
        const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
        return selCourseName === correspondingMention;
      });
      
      if (hasMentionSelected) {
        return `No puedes seleccionar ${courseName} común porque ya tienes ${correspondingMention} seleccionada`;
      }
      
      return '';
    },

    // Método para manejar el click en time slots (mejora legibilidad)
    handleCourseTimeSlotClick(dayGroup, courseTimeSlot) {
      // Verificar si el curso está bloqueado (excluyendo menciones que solo se deseleccionan automáticamente)
      if (this.isMatematicaElectivoBlocked(courseTimeSlot.courseId) || 
          this.isCommonCourseBlocked(courseTimeSlot.courseId)) {
        return; // No hacer nada si está bloqueado
      }
      
      // Verificar si es Matemática (M2) Electivo y no tiene Matemática (M1) seleccionada
      const courseName = this.getSubjectNameByCourseId(String(courseTimeSlot.courseId));
      let shouldShowModal = false;
      
      // Realizar la selección directamente
      const isCourseSelected = courseTimeSlot.allSchedules.some(s => this.isScheduleSelected(s.courseId, s.id));
      
      if (isCourseSelected) {
        // Deseleccionar todos los horarios de este curso
        courseTimeSlot.allSchedules.forEach(schedule => {
          const cIdStr = String(schedule.courseId);
          const sIdStr = String(schedule.id);
          this.localSelectedCourses = this.localSelectedCourses.filter(sel => 
            !(String(sel.courseId) === cIdStr && String(sel.scheduleId) === sIdStr)
          );
        });
      } else {
        // Solo mostrar modal si es Matemática (M2) Electivo y no tiene Matemática (M1)
        if (this.isMatematicaElectivo(courseName)) {
          const hasMatematicaM1 = this.localSelectedCourses.some(sel => {
            const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
            return this.isMatematicaTradicional(selCourseName);
          });
          
          if (!hasMatematicaM1) {
            shouldShowModal = true;
          }
        }
        
         // Deseleccionar TODOS los horarios del mismo NOMBRE DE CURSO antes de seleccionar
         this.localSelectedCourses = this.localSelectedCourses.filter(sel => {
           const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
           return selCourseName !== courseName;
         });
         
         // Si se selecciona una mención, deseleccionar automáticamente otras menciones y cursos comunes correspondientes
         const mentionCourses = ['Biología Mención', 'Física Mención', 'Química Mención'];
         const mentionToCommonMap = {
           'Biología Mención': 'Biología',
           'Física Mención': 'Física',
           'Química Mención': 'Química'
         };
         
         if (mentionCourses.includes(courseName)) {
           // PASO 1: Deseleccionar cualquier otra mención seleccionada
           const otherMentionsToDeselect = this.localSelectedCourses.filter(sel => {
             const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
             return mentionCourses.includes(selCourseName) && selCourseName !== courseName;
           });
           
           otherMentionsToDeselect.forEach(sel => {
             const course = this.findCourseById(sel.courseId);
             if (course) {
               const subjectName = course.subjectName || course.name || '';
               this.releaseCourseColor(sel.courseId, subjectName);
             }
           });
           
           this.localSelectedCourses = this.localSelectedCourses.filter(sel => {
             const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
             return !mentionCourses.includes(selCourseName) || selCourseName === courseName;
           });
           
           // PASO 2: Deseleccionar el curso común correspondiente a la nueva mención
           const correspondingCommon = mentionToCommonMap[courseName];
           if (correspondingCommon) {
             const commonCoursesToDeselect = this.localSelectedCourses.filter(sel => {
               const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
               return selCourseName === correspondingCommon;
             });
             
             commonCoursesToDeselect.forEach(sel => {
               const course = this.findCourseById(sel.courseId);
               if (course) {
                 const subjectName = course.subjectName || course.name || '';
                 this.releaseCourseColor(sel.courseId, subjectName);
               }
             });
             
             this.localSelectedCourses = this.localSelectedCourses.filter(sel => {
               const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
               return selCourseName !== correspondingCommon;
             });
           }
         }
        
        // VERIFICAR CONFLICTOS DE HORARIOS antes de seleccionar (revisar todos los horarios del curso)
        let conflict = null;
        let conflictingSchedule = null;
        for (const sch of courseTimeSlot.allSchedules) {
          const cIdStr = String(sch.courseId);
          const sIdStr = String(sch.id);
          const c = this.checkScheduleConflict(cIdStr, sIdStr);
          if (c) {
            conflict = c;
            conflictingSchedule = sch;
            break;
          }
        }
        if (conflict && conflictingSchedule) {
          // Mostrar modal de conflicto
          this.showConflictModal = true;
          this.conflictData = conflict;
          this.pendingScheduleSelection = { 
            courseId: String(conflictingSchedule.courseId), 
            scheduleId: String(conflictingSchedule.id), 
            isCourseTimeSlot: true, 
            dayGroup: dayGroup, 
            courseTimeSlot: courseTimeSlot,
            matchingSchedules: courseTimeSlot.allSchedules
          };
          return;
        }
        
        // Seleccionar TODOS los horarios del curso (para que aparezcan en todos los días)
        courseTimeSlot.allSchedules.forEach(schedule => {
          this.localSelectedCourses.push({ 
            courseId: String(schedule.courseId), 
            scheduleId: String(schedule.id) 
          });
        });
      }
      
      // Emitir el evento para el componente padre
      this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
      
      // Forzar la actualización de la vista
      this.$forceUpdate();
      
      // Mostrar modal después de la selección si es necesario (solo al seleccionar, no al deseleccionar)
      if (shouldShowModal && !isCourseSelected) {
        this.$nextTick(() => {
          this.showMatematicaElectivoModal = true;
        });
      }
    },
    
    checkScheduleConflict(newCourseId, newScheduleId) {
      const newCourse = this.findCourseById(newCourseId);
      const newSchedule = newCourse?.Schedules?.find(s => s.id == newScheduleId);
      
      if (!newSchedule) return null;
      
      // Verificar conflictos con horarios ya seleccionados
      for (const selection of this.localSelectedCourses) {
        const existingCourse = this.findCourseById(selection.courseId);
        const existingSchedule = existingCourse?.Schedules?.find(s => s.id == selection.scheduleId);
        
        if (existingSchedule && this.schedulesOverlap(newSchedule, existingSchedule)) {
          return {
            existingCourse: existingCourse,
            existingSchedule: existingSchedule,
            newCourse: newCourse,
            newSchedule: newSchedule
          };
        }
      }
      
      return null;
    },
    
    schedulesOverlap(schedule1, schedule2) {
      // Verificar si los horarios se superponen en el mismo día
      if (schedule1.dayOfWeek !== schedule2.dayOfWeek) return false;
      
      const start1 = this.timeToMinutes(schedule1.startTime);
      const end1 = this.timeToMinutes(schedule1.endTime);
      const start2 = this.timeToMinutes(schedule2.startTime);
      const end2 = this.timeToMinutes(schedule2.endTime);
      
      return (start1 < end2 && end1 > start2);
    },
    
    timeToMinutes(timeString) {
      const [hours, minutes] = timeString.split(':').map(Number);
      return hours * 60 + minutes;
    },
    

    // Métodos de utilidad
    formatTime(timeString) {
      if (!timeString) return '';
      return timeString.slice(0, 5); // Extrae HH:MM de HH:MM:SS
    },
    
    getRandomColor: (uniqueId) => {
      // Usar el store centralizado para obtener colores únicos
      const parts = uniqueId.toString().split('-')
      const courseId = parts[0]
      const subjectName = parts.slice(1).join('-') || ''
      return courseColorsStore.getCourseColor(courseId, subjectName)
    },
    
    generateHash(str) {
      let hash = 0;
      const string = str.toString();
      
      for (let i = 0; i < string.length; i++) {
        const char = string.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convertir a entero de 32 bits
      }
      
      return Math.abs(hash);
    },
    
    // Método para verificar si quedan últimos cupos
    isLastSeats(schedule) {
      // Buscar la materia que contiene este horario
      const materia = this.findMateriaBySchedule(schedule);
      if (!materia) return false;
      
      // Verificar si los cupos disponibles son menores al umbral
      const availableSeats = materia.availableSeats || 0;
      const lastSeatsThreshold = 10; // Umbral de últimos cupos
      
      return availableSeats < lastSeatsThreshold && availableSeats > 0;
    },
    
    // Método auxiliar para encontrar la materia por horario
    findMateriaBySchedule(schedule) {
      // Buscar en las materias originales para evitar recursión
      const allMaterias = [...this.leftColumnMaterias, ...this.rightColumnMaterias];
      
      for (const materia of allMaterias) {
        if (materia.courses) {
          for (const course of materia.courses) {
            if (course.Schedules) {
              const foundSchedule = course.Schedules.find(s => 
                s.id === schedule.id && s.courseId === schedule.courseId
              );
              if (foundSchedule) {
                return materia;
              }
            }
          }
        }
      }
      return null;
    },
    
    getScheduleDisplayText(schedule) {
      return `${this.formatTime(schedule.startTime)} - ${this.formatTime(schedule.endTime)}`;
    },
    
    getSelectedScheduleInfo(courseId) {
      const selection = this.localSelectedCourses.find(sel => String(sel.courseId) === String(courseId));
      if (!selection) return null;
      
      const course = this.findCourseById(selection.courseId);
      if (!course) return null;
      
      const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
      if (!schedule) return null;
      
      return {
        course: course,
        schedule: schedule,
        displayText: this.getScheduleDisplayText(schedule)
      };
    },
    
    isAnyScheduleSelected(courseId) {
      return this.localSelectedCourses.some(sel => String(sel.courseId) === String(courseId));
    },
    
    // Método para obtener el horario seleccionado de un curso específico
    getSelectedScheduleForCourse(courseId) {
      const selection = this.localSelectedCourses.find(sel => String(sel.courseId) === String(courseId));
      if (!selection) return null;
      
      const course = this.findCourseById(courseId);
      if (!course) return null;
      
      const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
      return schedule;
    },
    
    hasSchedulesForCourse(course) {
      return course.Schedules && course.Schedules.length > 0;
    },
    
    getCourseStatus(course) {
      if (!this.hasSchedulesForCourse(course)) {
        return 'no-schedules';
      }
      
      const hasSelected = this.localSelectedCourses.some(sel => String(sel.courseId) === String(course.id));
      return hasSelected ? 'selected' : 'available';
    },
    
    getCourseTooltip(course) {
      if (!this.hasSchedulesForCourse(course)) {
        return 'Este curso no tiene horarios disponibles para el mes seleccionado';
      }
      
      const selectedSchedule = this.getSelectedScheduleForCourse(course.id);
      if (selectedSchedule) {
        return `Horario seleccionado: ${this.formatTime(selectedSchedule.startTime)} - ${this.formatTime(selectedSchedule.endTime)} ${selectedSchedule.dayOfWeek}`;
      }
      
      return `Disponible con ${course.Schedules.length} horario(s) - Selecciona un horario`;
    },
    
    // Método para deseleccionar el horario de un curso
    deselectCourseSchedule(courseId) {
      
      // Convertir a string para comparación más robusta
      const courseIdStr = String(courseId);
      
      // Filtrar y remover el horario seleccionado de este curso
      const updatedSelectedCourses = this.localSelectedCourses.filter(sel => 
        String(sel.courseId) !== courseIdStr
      );
      
      // Solo actualizar si hay cambios
      if (updatedSelectedCourses.length !== this.localSelectedCourses.length) {
        this.localSelectedCourses = updatedSelectedCourses;
        
        // Emitir el evento para el componente padre
        this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
        

      }
    },
    
    // Métodos para el calendario semanal
    hasScheduleAtTime(day, time) {
      const timeHour = parseInt(time.split(':')[0]);
      
      for (const selection of this.localSelectedCourses) {
        const course = this.findCourseById(selection.courseId);
        if (!course) continue;
        
        const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
        if (!schedule || schedule.dayOfWeek !== day) continue;
        
        const startHour = parseInt(schedule.startTime.split(':')[0]);
        const endHour = parseInt(schedule.endTime.split(':')[0]);
        
        // Verificar si la hora actual está dentro del rango del horario
        if (timeHour >= startHour && timeHour < endHour) {
          return true;
        }
      }
      
      return false;
    },
    
    getScheduleColor(day, time) {
      const timeHour = parseInt(time.split(':')[0]);
      
      for (const selection of this.localSelectedCourses) {
        const course = this.findCourseById(selection.courseId);
        if (!course) continue;
        
        const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
        if (!schedule) continue;
        
        // Verificar si el horario se aplica a este día específico
        if (!this.scheduleAppliesToDay(schedule, day)) continue;
        
        const startHour = parseInt(schedule.startTime.split(':')[0]);
        const endHour = parseInt(schedule.endTime.split(':')[0]);
        
        if (timeHour >= startHour && timeHour < endHour) {
          const subjectName = course.subjectName || course.name || course.Subject?.name || '';
          return this.getRandomColor(`${course.id}-${subjectName}`);
        }
      }
      
      return '#3691D8'; // Color por defecto
    },
    
    getScheduleCourseName(day, time) {
      const timeHour = parseInt(time.split(':')[0]);
      
      for (const selection of this.localSelectedCourses) {
        const course = this.findCourseById(selection.courseId);
        if (!course) continue;
        
        const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
        if (!schedule) continue;
        
        // Verificar si el horario se aplica a este día específico
        if (!this.scheduleAppliesToDay(schedule, day)) continue;
        
        const startHour = parseInt(schedule.startTime.split(':')[0]);
        const endHour = parseInt(schedule.endTime.split(':')[0]);
        
        if (timeHour >= startHour && timeHour < endHour) {
          // Obtener la materia (Subject) a la que pertenece el curso
          const materia = this.findMateriaByCourseId(selection.courseId);
          const originalSubjectName = materia?.subjectName || course.subjectName || course.name || '';
          
          // Detectar etiquetas basándose en el nombre del curso
          const courseName = course.name || '';
          const hasMention = /MENCIÓN/gi.test(courseName) || /Mención/gi.test(courseName);
          const isElective = /ELECTIVO/gi.test(courseName) || /Electivo/gi.test(courseName);
          const isCommon = /COMÚN/gi.test(courseName) || /Común/gi.test(courseName);
          
          // Crear objeto curso con etiquetas basadas en el nombre
          const courseWithLabels = {
            ...course,
            hasMention: hasMention,
            isElective: isElective,
            isCommon: isCommon
          };
          
          // Usar utilidad compartida para formatear nombre completo
          return formatFullCourseName(originalSubjectName, courseWithLabels);
        }
      }
      
      return '';
    },
    
    getScheduleTimeRange(day, time) {
      const timeHour = parseInt(time.split(':')[0]);
      
      for (const selection of this.localSelectedCourses) {
        const course = this.findCourseById(selection.courseId);
        if (!course) continue;
        
        const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
        if (!schedule) continue;
        
        // Verificar si el horario se aplica a este día específico
        if (!this.scheduleAppliesToDay(schedule, day)) continue;
        
        const startHour = parseInt(schedule.startTime.split(':')[0]);
        const endHour = parseInt(schedule.endTime.split(':')[0]);
        
        if (timeHour >= startHour && timeHour < endHour) {
          return `${this.formatTime(schedule.startTime)} - ${this.formatTime(schedule.endTime)}`;
        }
      }
      
      return '';
    },
    
    // Verificar si es la hora de inicio de un horario
    isScheduleStartTime(day, time) {
      const timeHour = parseInt(time.split(':')[0]);
      
      for (const selection of this.localSelectedCourses) {
        const course = this.findCourseById(selection.courseId);
        if (!course) continue;
        
        const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
        if (!schedule) continue;
        
        // Verificar si el horario se aplica a este día específico
        if (!this.scheduleAppliesToDay(schedule, day)) continue;
        
        const startHour = parseInt(schedule.startTime.split(':')[0]);
        
        if (timeHour === startHour) {
          return true;
        }
      }
      
      return false;
    },
    
    // Método para verificar si un horario se aplica a un día específico
    scheduleAppliesToDay(schedule, day) {
      // Verificar coincidencia directa del día del horario
      return schedule.dayOfWeek === day;
    },
    
    // Método auxiliar para encontrar un curso por ID de horario
    findCourseByScheduleId(scheduleId) {
      for (const materia of [...this.leftColumnMaterias, ...this.rightColumnMaterias]) {
        for (const course of materia.courses) {
          if (course.Schedules?.some(s => s.id == scheduleId)) {
            return course;
          }
        }
      }
      return null;
    },
    
    // Obtener el grid-row para extender el bloque de horario con precisión de minutos
    getScheduleGridRow(day, time) {
      const timeHour = parseInt(time.split(':')[0]);
      
      for (const selection of this.localSelectedCourses) {
        const course = this.findCourseById(selection.courseId);
        if (!course) continue;
        
        const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
        if (!schedule) continue;
        
        // Verificar si el horario se aplica a este día específico
        if (!this.scheduleAppliesToDay(schedule, day)) continue;
        
        const startHour = parseInt(schedule.startTime.split(':')[0]);
        
        if (timeHour === startHour) {
          const startIndex = this.timeSlots.indexOf(`${startHour}:00`) + 1;
          
          // Calcular la duración exacta en fracciones de hora
          const startMinutes = parseInt(schedule.startTime.split(':')[1]) || 0;
          const endMinutes = parseInt(schedule.endTime.split(':')[1]) || 0;
          const endHour = parseInt(schedule.endTime.split(':')[0]);
          
          // Calcular cuántas filas completas ocupa
          const totalMinutes = (endHour * 60 + endMinutes) - (startHour * 60 + startMinutes);
          const span = Math.ceil(totalMinutes / 60);
          
          return `${startIndex} / span ${span}`;
        }
      }
      
      return '';
    },
    
    // Obtener el estilo de posicionamiento vertical para precisión de minutos
    getScheduleVerticalPosition(day, time) {
      const timeHour = parseInt(time.split(':')[0]);
      
      for (const selection of this.localSelectedCourses) {
        const course = this.findCourseById(selection.courseId);
        if (!course) continue;
        
        const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
        if (!schedule) continue;
        
        // Verificar si el horario se aplica a este día específico
        if (!this.scheduleAppliesToDay(schedule, day)) continue;
        
        const startHour = parseInt(schedule.startTime.split(':')[0]);
        
        if (timeHour === startHour) {
          const startMinutes = parseInt(schedule.startTime.split(':')[1]) || 0;
          const endMinutes = parseInt(schedule.endTime.split(':')[1]) || 0;
          const endHour = parseInt(schedule.endTime.split(':')[0]);
          
          // Calcular la posición vertical basada en los minutos
          const topOffset = (startMinutes / 60) * 100; // Porcentaje desde la parte superior
          const totalMinutes = (endHour * 60 + endMinutes) - (startHour * 60 + startMinutes);
          const height = (totalMinutes / 60) * 100; // Altura en porcentaje
          
          return {
            top: `${topOffset}%`,
            height: `${height}%`,
            minHeight: '40px'
          };
        }
      }
      
      return {};
    },
    
    // Métodos para manejar el modal de conflicto
    closeConflictModal() {
      this.showConflictModal = false;
      this.conflictData = {};
      this.pendingScheduleSelection = null;
    },
    
    // Métodos para manejar el modal de Matemática Electivo
    closeMatematicaElectivoModal() {
      this.showMatematicaElectivoModal = false;
      this.pendingScheduleSelection = null;
    },
    
    
    // Método para validar si se puede continuar
    validateCanContinue() {
      // Verificar si hay Matemática (M2) Electivo seleccionada sin Matemática (M1)
      const hasMatematicaElectivo = this.localSelectedCourses.some(sel => {
        const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
        return this.isMatematicaElectivo(selCourseName);
      });
      
      const hasMatematicaM1 = this.localSelectedCourses.some(sel => {
        const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
        return this.isMatematicaTradicional(selCourseName);
      });
      
      // Si tiene Matemática (M2) Electivo pero no tiene Matemática (M1), no puede continuar
      if (hasMatematicaElectivo && !hasMatematicaM1) {
        return false;
      }
      
      return true;
    },
    
    confirmScheduleReplacement() {
      if (this.pendingScheduleSelection) {
        
        // PASO 1: Remover el curso que causa conflicto
        const existingCourseId = String(this.conflictData.existingCourse.id);
        this.localSelectedCourses = this.localSelectedCourses.filter(sel => 
          String(sel.courseId) !== existingCourseId
        );
        
        // PASO 2: Usar el método apropiado para seleccionar el nuevo horario (omitir validaciones ya que ya se resolvió el conflicto)
        if (this.pendingScheduleSelection.isTimeSlot || this.pendingScheduleSelection.isCourseTimeSlot) {
          // Para timeSlots, usar el método de grupo para seleccionar todos los horarios
          const allScheduleIds = this.pendingScheduleSelection.matchingSchedules.map(s => s.id);
          const newCourseId = this.pendingScheduleSelection.matchingSchedules[0].courseId;
          this.selectScheduleGroupForCourse(newCourseId, allScheduleIds, true); // skipValidation = true
        } else if (this.pendingScheduleSelection.isGroup && this.pendingScheduleSelection.allScheduleIds) {
          // Para grupos con allScheduleIds
          const newCourseId = this.pendingScheduleSelection.courseId;
          const allScheduleIds = this.pendingScheduleSelection.allScheduleIds;
          this.selectScheduleGroupForCourse(newCourseId, allScheduleIds, true); // skipValidation = true
        } else {
          // Para horarios individuales
          const newCourseId = this.pendingScheduleSelection.courseId;
          const newScheduleId = this.pendingScheduleSelection.scheduleId;
          this.selectScheduleForCourse(newCourseId, newScheduleId, true); // skipValidation = true
        }
        
        // PASO 3: Validar si el nuevo curso es Matemática (M2) Electivo sin Matemática (M1)
        const newCourseName = this.getSubjectNameByCourseId(String(this.pendingScheduleSelection.courseId));
        if (this.isMatematicaElectivo(newCourseName)) {
          const hasMatematicaM1 = this.localSelectedCourses.some(sel => {
            const selCourseName = this.getSubjectNameByCourseId(String(sel.courseId));
            return this.isMatematicaTradicional(selCourseName);
          });
          
          if (!hasMatematicaM1) {
            // Mostrar modal de validación después del conflicto
            this.$nextTick(() => {
              this.showMatematicaElectivoModal = true;
            });
          }
        }
        
        // Cerrar el modal de conflicto
        this.closeConflictModal();
      }
    },
    
    // Método para verificar si un horario tiene conflicto
    hasScheduleConflict(courseId, scheduleId) {
      const courseIdStr = String(courseId);
      const scheduleIdStr = String(scheduleId);
      
      const newCourse = this.findCourseById(courseIdStr);
      const newSchedule = newCourse?.Schedules?.find(s => s.id == scheduleIdStr);
      
      if (!newSchedule) return false;
      
      // Verificar conflictos con horarios ya seleccionados
      for (const selection of this.localSelectedCourses) {
        const existingCourse = this.findCourseById(selection.courseId);
        const existingSchedule = existingCourse?.Schedules?.find(s => s.id == selection.scheduleId);
        
        if (existingSchedule && this.schedulesOverlap(newSchedule, existingSchedule)) {
          return true;
        }
      }
      
      return false;
    },

    // Método para validar que solo hay 1 horario por nombre de curso
    validateSingleSchedulePerCourseName() {
      const courseNameCounts = {};
      
      for (const selection of this.localSelectedCourses) {
        const courseName = this.getSubjectNameByCourseId(String(selection.courseId));
        if (courseName) {
          courseNameCounts[courseName] = (courseNameCounts[courseName] || 0) + 1;
        }
      }
      
      const violations = Object.entries(courseNameCounts).filter(([, count]) => count > 1);
      
      if (violations.length > 0) {
        return false;
      }
      
      return true;
    },

    // Método para limpiar horarios duplicados por nombre de curso (emergencia)
    cleanupDuplicateSchedulesByCourseName() {
      const seen = new Set();
      const cleaned = [];
      
      // Primero eliminar duplicados exactos
      for (const selection of this.localSelectedCourses) {
        const key = `${selection.courseId}-${selection.scheduleId}`;
        if (!seen.has(key)) {
          seen.add(key);
          cleaned.push(selection);
        }
      }
      
      // Luego mantener solo el primer horario de cada nombre de curso
      const finalCleaned = [];
      const courseNames = new Set();
      
      for (const selection of cleaned) {
        const courseName = this.getSubjectNameByCourseId(String(selection.courseId));
        if (courseName && !courseNames.has(courseName)) {
          courseNames.add(courseName);
          finalCleaned.push(selection);
        }
      }
      
      this.localSelectedCourses = finalCleaned;
      this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
      this.$forceUpdate();
    },

    // Método para forzar limpieza (llamar desde consola si es necesario)
    forceCleanup() {
      this.cleanupDuplicateSchedulesByCourseName();
    },

    // Método para mostrar el estado actual por nombre de curso
    showCurrentStateByCourseName() {
      const courseNameCounts = {};
      
      for (const selection of this.localSelectedCourses) {
        const courseName = this.getSubjectNameByCourseId(String(selection.courseId));
        if (courseName) {
          if (!courseNameCounts[courseName]) {
            courseNameCounts[courseName] = [];
          }
          courseNameCounts[courseName].push({
            courseId: selection.courseId,
            scheduleId: selection.scheduleId
          });
        }
      }
    },
    // Método para testing - forzar selección de un horario
    forceSelectSchedule() {
      if (this.leftColumnMaterias.length > 0 && this.leftColumnMaterias[0].courses.length > 0) {
        const course = this.leftColumnMaterias[0].courses[0];
        if (course.Schedules && course.Schedules.length > 0) {
          const schedule = course.Schedules[0];
          this.toggleSchedule(course.id, schedule.id);
        }
      }
    },

    // Método para testing - forzar selección de múltiples horarios
    forceSelectMultipleSchedules() {
      
      // Buscar un curso que tenga múltiples horarios
      const allMaterias = [...this.leftColumnMaterias, ...this.rightColumnMaterias];
      for (const materia of allMaterias) {
        if (materia.courses) {
          for (const course of materia.courses) {
            if (course.Schedules && course.Schedules.length > 1) {
              // Seleccionar todos los horarios de este curso
              const scheduleIds = course.Schedules.map(s => s.id);
              this.selectScheduleGroupForCourse(course.id, scheduleIds);
              return;
            }
          }
        }
      }
    },

    // Método para testing - agregar horarios manualmente
    forceAddMultipleSchedules() {
      
      // Limpiar horarios existentes
      this.localSelectedCourses = [];
      
      // Buscar un curso con múltiples horarios
      const allMaterias = [...this.leftColumnMaterias, ...this.rightColumnMaterias];
      for (const materia of allMaterias) {
        if (materia.courses) {
          for (const course of materia.courses) {
            if (course.Schedules && course.Schedules.length > 1) {
              
              // Agregar cada horario manualmente
              course.Schedules.forEach((schedule) => {
                this.localSelectedCourses.push({
                  courseId: String(course.id),
                  scheduleId: String(schedule.id)
                });
              });
              // Emitir evento y actualizar vista
              this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
              this.$forceUpdate();
              return;
            }
          }
        }
      }
      
    },

    deselectAllSchedulesForCourse(courseId) {
      this.localSelectedCourses = this.localSelectedCourses.filter(sel => String(sel.courseId) !== String(courseId));
      this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
    },
    
    deselectAllSchedulesForSubject(subjectName) {
      // Obtener todos los cursos de esta materia usando las materias originales
      const allMaterias = [...this.leftColumnMaterias, ...this.rightColumnMaterias];
      const materias = allMaterias.filter(m => m.subjectName === subjectName);
      
      if (materias.length > 0) {
        // Deseleccionar todos los horarios de todos los cursos de esta materia
        const courseIds = materias.flatMap(materia => 
          materia.courses ? materia.courses.map(course => String(course.id)) : []
        );
        this.localSelectedCourses = this.localSelectedCourses.filter(sel => 
          !courseIds.includes(String(sel.courseId))
        );
        this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
      }
    },

    deselectAllSchedulesForUniqueKey(uniqueKey) {
      // Extraer el courseId de la clave única
      const courseId = uniqueKey.split('_')[1];
      if (courseId) {
        // Liberar el color del curso antes de deseleccionarlo
        const course = this.findCourseById(courseId);
        if (course) {
          const subjectName = course.subjectName || course.name || '';
          courseColorsStore.releaseColor(courseId, subjectName);
        }
        
        // Deseleccionar solo los horarios de este curso específico
        this.localSelectedCourses = this.localSelectedCourses.filter(sel => 
          String(sel.courseId) !== courseId
        );
        this.$emit('update:selectedCourses', [...this.localSelectedCourses]);
      }
    },
    
    // Método para liberar colores cuando se deseleccionen cursos
    releaseCourseColor(courseId, subjectName) {
      courseColorsStore.releaseColor(courseId, subjectName);
    },
    
    // Método para limpiar todos los colores
    clearAllColors() {
      courseColorsStore.releaseAllColors();
    },
    
    // Método para forzar reasignación de colores con nueva paleta
    forceReassignColors() {
      courseColorsStore.forceReassignColors();
      this.$forceUpdate();
    },
    
    // Método de debug para verificar colores
    debugColors() {
      // Debug method removed
    },
    
    // Método para comparar colores con el calendario
    compareColorsWithCalendar() {
      // Debug method removed
    }
  },
  beforeUnmount() {
    // Limpiar todos los colores asignados cuando se desmonte el componente
    this.clearAllColors();
  },
  components: {
    WeeklyCalendar
  }
}
</script>

<style scoped>

.cursos-section-container {
  background: white;
  border-radius: 8px;
}


/* Estilos para la sección de cursos */
.cursos-section {
  background: white;
  border-radius: 8px;
  padding: 24px;
}

.section-content {
  width: 100%;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  font-family:  'Poppins-Medium' !important; ;
}

.section-subtitle {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.4;
  font-family:  'Poppins-Medium' !important; ;
}

/* Loading and Error States */
.loading-courses {
  text-align: center;
  padding: 40px 20px;
}

.no-courses-message {
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}

.no-schedules-message {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-style: italic;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin: 10px 0;
  border: 1px dashed #dee2e6;
}

/* Estilos para horarios disponibles */
.available-schedules {
  padding: 8px 0;
}

.schedule-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  justify-content: flex-start;
  width: 100%;
  box-sizing: border-box;
}

.schedule-column {
  display: flex;
  flex-direction: column;
  min-width: 0; /* Permite que el contenido se ajuste */
  width: fit-content;
}

.column-header {
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  text-align: left;
}

.day-header {
  padding: 4px 4px;
  color: #333;
  font-weight: normal;
  transition: background-color 0.2s ease;
  display: inline-block;
  font-size: 13px;
}

.day-header.active {
  color: white;
}

.column-times {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  flex: 1;
  min-width: 0;
}

/* Time Slot Buttons */
.time-slot-btn {
  padding: 0px 4px 0px 4px;
  border: 2px solid #e9e9e9;
  border-radius: 8px;
  background-color: #e9e9e9;
  color: #333;
  font-family:  'Poppins-Medium' !important;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  letter-spacing: 0px;
  cursor: pointer;
  transition: none;
  text-align: center;
  min-height: 34px;
  min-width: 105px;
  width: fit-content;
  max-width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
  overflow: hidden;
  box-sizing: border-box;
}

/* Estilos para botones bloqueados */
.time-slot-btn.blocked {
  background-color: #f5f5f5 !important;
  border-color: #d0d0d0 !important;
  color: #999 !important;
  cursor: not-allowed !important;
  opacity: 0.6;
  position: relative;
}

/* Estilo específico para cursos comunes bloqueados por mención */
.time-slot-btn.blocked::after {
  content: "🔒";
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 10px;
  opacity: 0.7;
}

.time-slot-btn.blocked:hover {
  background-color: #f5f5f5 !important;
  border-color: #d0d0d0 !important;
  color: #999 !important;
  transform: none !important;
  box-shadow: none !important;
}

/* Multi-time slot buttons (for courses with multiple schedules) */
.multi-time-slot {
  min-height: 60px;
  padding: 8px 10px;
  white-space: normal;
  word-wrap: break-word;
  overflow: visible;
}

/* Ajustar altura mínima cuando hay conflicto */
.multi-time-slot.conflict {
  min-height: 80px;
  padding: 6px 8px;
}

.time-text {
  font-family:  'Poppins-Medium' !important;
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
  letter-spacing: 0px; 
  text-align: center;
  white-space: normal;
  word-wrap: break-word;
  max-width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.time-text span {
  line-height: 1.8;
}

/* Estilos específicos para íconos de conflicto en botones multi-time */
.multi-time-slot .warning-icon {
  position: absolute;
  top: 4px;
  right: 4px;
  font-size: 0.9em;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 2px;
  line-height: 1;
}

/* Estilos para botones que solo muestran la hora (sin días) */
.single-time-slot {
  font-family:  'Poppins-Medium' !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 22px !important;
  letter-spacing: 0px !important;
}

/* Hover desactivado para .time-slot-btn */

.time-slot-btn.active {
  color: white !important;
  border-color: transparent !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transform: translateY(-1px);
  font-weight: 600;
}

/* Asegurar que el color de fondo se aplique correctamente */
.time-slot-btn.active[style*="background-color"] {
  color: white !important;
  border-color: transparent !important;
}

/* Estilos para horarios seleccionados */
.time-slot-btn.selected {
  color: white !important;
  border-color: transparent !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transform: translateY(-1px);
  font-weight: 600;
}

.time-slot-btn.selected[style*="background-color"] {
  color: white !important;
  border-color: transparent !important;
}

/* Estilos específicos para cuando se aplica color de fondo */
.time-slot-btn[style*="background-color"] {
  color: white !important;
  border-color: transparent !important;
  font-weight: 600;
}

/* Animación para horarios seleccionados */
.time-slot-btn.selected {
  animation: pulse 0.3s ease-in-out;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

/* Estilo para horarios sin duplicados */
.time-slot-btn.unique {
  border-color: #3691D8;
  background-color: #f8f9fa;
}

.time-slot-btn.unique:hover {
  background-color: #e3f2fd;
  border-color: #0056b3;
}

/* New styles for courses grid */
.courses-grid {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.course-item {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  background-color: white;
  margin-bottom: 2px;
  transition: all 0.3s ease;
}

.course-button {
  width: 100%;
  padding: 10px 16px;
  background-color: #f8f9fa;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #333;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.course-button:hover {
  background-color: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.course-button-active {
  color: white;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.course-button-inactive {
  background-color: #f8f9fa;
  color: #333;
}

/* Cuando el botón está inactivo pero tiene horarios seleccionados (fondo azul) */
.course-button-inactive[style*="background-color: #3691D8"] {
  color: white !important;
}

.course-button-inactive[style*="background-color: #3691D8"] span {
  color: white !important;
}

.course-button-inactive[style*="background-color: #3691D8"] .course-arrow {
  color: white !important;
}

.course-button-inactive[style*="background-color: #3691D8"] svg path {
  stroke: white !important;
}

/* Asegurar que el texto sea blanco cuando el botón tiene color de fondo azul */
.course-button[style*="background-color: #3691D8"] {
  color: white !important;
}

.course-button[style*="background-color: #3691D8"] .course-arrow {
  color: white !important;
}

/* Reglas más específicas para asegurar que el texto sea blanco */
.course-button[style*="background-color: #3691D8"] span {
  color: white !important;
}

.course-button[style*="background-color: #3691D8"] svg {
  color: white !important;
}

.course-button[style*="background-color: #3691D8"] svg path {
  stroke: white !important;
}

/* Regla adicional para cuando el botón tiene el estilo inline */
.course-button[style*="background-color: #3691D8"] * {
  color: white !important;
}


/* Reglas limpias para botones con fondo azul #3691D8 */
.course-button[style*="background-color: #3691D8"] {
  color: white !important;
}

.course-button[style*="background-color: #3691D8"] span {
  color: white !important;
}

.course-button[style*="background-color: #3691D8"] .course-arrow {
  color: white !important;
}

.course-button[style*="background-color: #3691D8"] svg path {
  stroke: white !important;
}

/* Regla universal para cualquier elemento dentro del botón con fondo azul */
.course-button[style*="background-color: #3691D8"] * {
  color: white !important;
}

.course-arrow {
  transition: transform 0.3s ease;
  color: #2C2D30; /* Negro por defecto */
  width: 16px;
  height: 16px;
}

/* Asegurar que el icono sea blanco cuando el botón tiene fondo azul */
.course-button[style*="background-color: #3691D8"] .course-arrow {
  color: white !important;
}

.course-button-active .course-arrow {
  color: white !important;
}

/* Regla más específica para cuando el botón está activo Y tiene fondo azul */
.course-button.course-button-active[style*="background-color: #3691D8"] .course-arrow {
  color: white !important;
}

/* Regla adicional con máxima especificidad */
button.course-button.course-button-active[style*="background-color: #3691D8"] .course-arrow {
  color: white !important;
}

.course-button-active {
  color: white;
}

.course-arrow.rotated {
  transform: rotate(180deg);
}

.course-schedule-options {
  padding: 10px;
  border: 1px solid #3691d8;
  background-color: #f8f9fa;
  border-radius: 0 0 12px 12px;
}

.course-item.no-schedules {
  opacity: 0.7;
  background-color: #f8f9fa;
}

.course-item.no-schedules .course-button {
  background-color: #e9ecef;
  color: #6c757d;
  cursor: not-allowed;
}

.course-item.no-schedules .course-button:hover {
  background-color: #e9ecef;
  transform: none;
}

.course-item.selected {
  border-color: #3691D8;
}

.debug-section {
  font-size: 12px;
}

/* Estilos para el calendario semanal */
.weekly-calendar-section {
  margin-top: 30px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
}

.calendar-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

.weekly-calendar {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.calendar-header {
  display: grid;
  grid-template-columns: 60px repeat(6, 1fr);
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.time-column-header {
  padding: 12px 8px;
  font-weight: 600;
  color: #333;
  text-align: center;
  border-right: 1px solid #e0e0e0;
}

.calendar-header .day-header {
  padding: 12px 8px;
  font-weight: 600;
  color: #333;
  text-align: center;
  border-right: 1px solid #e0e0e0;
}

.calendar-header .day-header:last-child {
  border-right: none;
}

.calendar-body {
  overflow-y: auto;
  overflow-x: visible;
  display: grid;
  grid-template-columns: 60px repeat(6, 1fr);
  grid-template-rows: repeat(19, 40px);
  position: relative;
}

.time-row {
  display: contents;
}

.time-label {
  padding: 8px;
  font-size: 0.8rem;
  color: #666;
  text-align: center;
  border-right: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
}

.calendar-cell {
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  min-height: 40px;
  position: relative;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.calendar-cell:nth-child(7n) {
  border-right: none;
}

.time-row:last-child .calendar-cell {
  border-bottom: none;
}

.schedule-block {
  position: absolute;
  padding: 6px 8px;
  color: white;
  font-size: 0.7rem;
  font-weight: 500;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  border-radius: 6px;
  margin: 1px;
  min-height: 40px;
  z-index: 10;
  left: 2px;
  right: 2px;
  box-sizing: border-box;
}

.schedule-course {
  font-weight: 600;
  margin-bottom: 2px;
  line-height: 1.2;
}

.schedule-time {
  font-size: 0.65rem;
  opacity: 0.9;
  line-height: 1.1;
}

/* Responsive para el calendario */
@media (max-width: 768px) {
  .weekly-calendar-section {
    padding: 15px;
    margin-top: 20px;
  }
  
  .calendar-header {
    grid-template-columns: 50px repeat(6, 1fr);
  }
  
  .time-row {
    grid-template-columns: 50px repeat(6, 1fr);
  }
  
  .time-column-header,
  .time-label {
    font-size: 0.7rem;
    padding: 6px 4px;
  }
  
  .calendar-header .day-header {
    font-size: 0.7rem;
    padding: 8px 4px;
  }
  
  .schedule-block {
    padding: 2px 4px;
    font-size: 0.6rem;
  }
  
  .schedule-course {
    font-size: 0.6rem;
  }
  
  .schedule-time {
    font-size: 0.55rem;
  }
}

/* Info about single schedule selection */
.selection-info {
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.info-text {
  margin: 0;
  color: #1976d2;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-icon {
  font-size: 1rem;
  opacity: 0.8;
}

/* Responsive styles */
@media (max-width: 768px) {
  .schedule-columns {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .schedule-column {
    min-width: 100%;
  }
  
  .time-slot-btn {
    padding: 6px 10px;
    font-size: 0.8rem;
    min-height: 32px;
  }
  
  .day-header {
    font-weight: 100 !important;
    padding: 8px 8px;
    font-size: 0.75rem;
  }
  
  .course-button {
    padding: 12px 14px;
    font-size: 0.85rem;
  }
  
  .course-schedule-options {
    padding: 14px;
  }
  
  .course-item {
    max-width: 100%;
  }
}

@media (max-width: 576px) {
  .courses-grid .row {
  flex-direction: column;
  gap: 2px;
}

.courses-grid .col-md-6 {
  width: 100%;
  margin-bottom: 0;
  padding: 0;
}
}

.time-slot-btn.conflict {
  background-color: #FFF8E1 !important;
  color: #F57C00 !important;
  border-color: #FFE082 !important;
  position: relative;
}
.warning-icon-inline {
  margin-right: 6px;
  font-size: 14px;
  vertical-align: middle;
  color: #F57C00;
}

/* Estilos para el header de conflicto */
.conflict-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.conflict-label {
  color: #F57C00;
  font-size: 10px;
  font-weight: 600;
}

/* Estilos para el contenido del tiempo cuando hay conflicto */
.time-content {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
}

.time-content.has-conflict {
  margin-top: 2px;
  padding-top: 2px;
  border-top: 1px solid rgba(245, 124, 0, 0.2);
}

/* Estilos para el mensaje de últimos cupos */
.time-text {
  font-family:  'Poppins-Medium' !important;
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
  letter-spacing: 0px;
  text-align: left;
  width: 100%;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
}

.last-seats-warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 0.8em;
  color: #000000;
  font-weight: 600;
  text-align: center;
  width: 100%;
  flex-direction: row;
  margin-left: 0;
  margin-right: 0;
}

.last-seats-warning.selected {
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.last-seats-warning .warning-icon {
  font-size: 1em;
}

.last-seats-warning .warning-text {
  font-size: 0.75em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.conflict-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.conflict-modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  max-width: 400px;
  width: 90vw;
  padding: 0;
  animation: modalIn 0.2s;
  overflow: hidden;
}
@keyframes modalIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.conflict-modal .modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 8px 20px;
  border-bottom: 1px solid #eee;
}
.conflict-modal .modal-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #856404;
  display: flex;
  align-items: center;
}
.conflict-modal .warning-icon {
  margin-right: 8px;
  font-size: 1.3em;
}
.conflict-modal .close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #888;
  cursor: pointer;
  line-height: 1;
}
.conflict-modal .modal-body {
  padding: 18px 20px 10px 20px;
  color: #333;
}
.conflict-modal .conflict-details {
  background: #fffbe6;
  border: 1px solid #ffe066;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 10px 0 14px 0;
  font-size: 0.97em;
}
.conflict-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px 16px 20px;
  border-top: 1px solid #eee;
  background: #fafafa;
}
.conflict-modal .btn {
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  border: none;
  cursor: pointer;
}
.conflict-modal .btn-secondary {
  background: #e9ecef;
  color: #333;
}
.conflict-modal .btn-warning {
  background: #ffe066;
  color: #856404;
  border: 1px solid #ffd700;
}
.conflict-modal .btn-warning:hover {
  background: #ffd700;
}

/* Inline Badge Styles - Badge dentro del botón */
.inline-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px; /* Solo padding horizontal, sin padding vertical */
  border-radius: 8px; /* Border radius 8px */
  font-size: 0.65rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border: 1.5px solid #2C2D30; /* Border 1.5px, color #2C2D30 */
  color: #2C2D30; /* Texto color #2C2D30 */
  margin: 0 18px;
  white-space: nowrap;
  transition: all 0.2s ease;
  vertical-align: middle;
  background-color: transparent; /* Fondo transparente */
  min-width: 70px; /* Ancho mínimo 70px */
  height: 24px; /* Altura fija 24px */
  box-sizing: border-box;
  line-height: 1; /* Resetear line-height */
}

/* Asegurar que el texto dentro del badge esté centrado */
.inline-badge * {
  line-height: 1;
  margin: 0;
  padding: 0;
}

/* Ajustar el botón del curso para flexbox */
.course-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
}

.course-button span:first-child {
  flex: 1;
  text-align: left;
}

.course-arrow {
  flex-shrink: 0;
  margin-left: auto;
}

/* Badge específico para MENCIÓN - Estado normal */
.badge-mencion {
  background-color: transparent !important;
  border-color: #2C2D30 !important;
  color: #2C2D30 !important;
}

/* Badge específico para ELECTIVO - Estado normal */
.badge-electivo {
  background-color: transparent !important;
  border-color: #2C2D30 !important;
  color: #2C2D30 !important;
}

/* Badge específico para Común - Estado normal */
.badge-comun {
  background-color: transparent !important;
  border-color: #2C2D30 !important;
  color: #2C2D30 !important;
}

/* Estilos para badges cuando el curso está activo/expandido */
.course-button[style*="background-color: #3691D8"] .inline-badge {
  border-color: white !important;
  color: white !important;
}

.course-button[style*="background-color: #3691D8"] .badge-mencion {
  border-color: white !important;
  color: white !important;
}

.course-button[style*="background-color: #3691D8"] .badge-electivo {
  border-color: white !important;
  color: white !important;
}

.course-button[style*="background-color: #3691D8"] .badge-comun {
  border-color: white !important;
  color: white !important;
}

/* Estilos adicionales para asegurar que se apliquen */
.course-item .course-button[style*="background-color: #3691D8"] .inline-badge {
  border-color: white !important;
  color: white !important;
}

.course-item .course-button[style*="background-color: #3691D8"] .badge-mencion {
  border-color: white !important;
  color: white !important;
}

.course-item .course-button[style*="background-color: #3691D8"] .badge-electivo {
  border-color: white !important;
  color: white !important;
}

.course-item .course-button[style*="background-color: #3691D8"] .badge-comun {
  border-color: white !important;
  color: white !important;
}

/* Estilos con máxima especificidad para inline-badge */
.course-item.active .course-button .inline-badge,
.course-item .course-button.active .inline-badge,
.course-item .course-button[style*="background-color: #3691D8"] .inline-badge,
.course-item .course-button[style*="background-color: rgb(54, 145, 216)"] .inline-badge {
  border-color: white !important;
  color: white !important;
  background-color: transparent !important;
}

.course-item.active .course-button .badge-mencion,
.course-item .course-button.active .badge-mencion,
.course-item .course-button[style*="background-color: #3691D8"] .badge-mencion,
.course-item .course-button[style*="background-color: rgb(54, 145, 216)"] .badge-mencion {
  border-color: white !important;
  color: white !important;
}

.course-item.active .course-button .badge-electivo,
.course-item .course-button.active .badge-electivo,
.course-item .course-button[style*="background-color: #3691D8"] .badge-electivo,
.course-item .course-button[style*="background-color: rgb(54, 145, 216)"] .badge-electivo {
  border-color: white !important;
  color: white !important;
}

.course-item.active .course-button .badge-comun,
.course-item .course-button.active .badge-comun,
.course-item .course-button[style*="background-color: #3691D8"] .badge-comun,
.course-item .course-button[style*="background-color: rgb(54, 145, 216)"] .badge-comun {
  border-color: white !important;
  color: white !important;
}

/* No Courses Warning Card - Exact match with ecommerce 2025 */
.no-courses-warning {
  width: 100%;
  margin: 20px 0;
}

.warning-card {
  background-color: #fef9e6;
  border: 1px solid #daa520;
  border-radius: 6px;
  padding: 16px;
  margin: 0;
  font-family: Arial, sans-serif;
}

.warning-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
}

.warning-icon {
  font-size: 16px;
  margin-right: 6px;
  color: #d4a017;
  margin-top: 2px;
  flex-shrink: 0;
}

.warning-title {
  font-weight: normal;
  color: #8B4513;
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
}

.unavailable-courses-list {
  margin: 8px 0 12px 0;
}

.unavailable-courses-list ul {
  list-style: none;
  padding: 0;
  margin: 0 0 0 20px;
}

.unavailable-courses-list li {
  color: #8B4513;
  font-size: 14px;
  font-weight: normal;
  padding: 1px 0;
  position: relative;
  line-height: 1.3;
}

.unavailable-courses-list li:before {
  content: "•";
  color: #8B4513;
  font-weight: normal;
  position: absolute;
  left: -12px;
  font-size: 14px;
}

.streaming-option {
  margin-top: 0;
  padding-top: 0;
  border: none;
}

.streaming-option p {
  color: #8B4513;
  margin: 0;
  font-size: 14px;
  font-weight: normal;
  line-height: 1.4;
  text-align: left;
}

.streaming-option strong {
  font-weight: normal;
}

/* Highlight streaming text */
.streaming-option .streaming-text {
  color: #d4a017;
  font-weight: bold;
}

/* FAQ Option */
.faq-option {
  margin-top: 8px;
  padding-top: 0;
  border: none;
}

.faq-option p {
  color: #8B4513;
  margin: 0;
  font-size: 14px;
  font-weight: normal;
  line-height: 1.4;
  text-align: left;
}

/* Highlight FAQ link */
.faq-option .faq-link {
  color: #d4a017;
  font-weight: bold;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s ease;
}

.faq-option .faq-link:hover {
  color: #b8860b;
}

.faq-option .faq-link:visited {
  color: #d4a017;
}

.faq-option .faq-link:active {
  color: #d4a017;
}

/* Responsive adjustments for warning card */
@media (max-width: 768px) {
  .no-courses-warning {
    margin: 15px 0;
  }
  
  .warning-card {
    padding: 14px;
  }
}

</style> 